import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  ButtonBase,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Stack,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Sync as SyncIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Note as NoteIcon,
  History as HistoryIcon,
  FolderOpen as DocumentsTabIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Description as DescriptionIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ContentCopy as CopyIcon,
  Sms as SmsIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import {
  CardIcon as BillingIcon,
  MessagesIcon as MessageIcon,
  AiIcon,
  DetailsIcon,
} from "../components/icons/CustomTabIcons";
import { format } from "date-fns";
import { Contact, FeeStructure, MessageAttachment } from "../types";
import {
  contactsApi,
  triageApi,
  zapriteApi,
} from "../services/api";
import PatientDetailsView from "../components/PatientDetails/PatientDetailsView";
import PatientClinicalStrip, {
  PatientClinicalStripHandle,
} from "../components/PatientDetails/PatientClinicalStrip";
import AIConsultationPanel, {
  SchedulingSuggestion,
} from "../components/PatientDetails/AIConsultationPanel";
import AppointmentModal from "../components/Calendar/AppointmentModal";
import api from "../services/api";
import { useSocket } from "../hooks/useSocket";
import { useAuthStore } from "../store/authStore";
import { useUiChromeStore } from "../store/uiChromeStore";
import { formatDateTime } from "../utils/dateTime";
import { useSearchParams } from "react-router-dom";
import { patientHeaderFacts } from "./patientHeaderFacts";

// Below this pane width the desktop patient header cannot fit the avatar,
// the name and the row of action buttons side by side (they need ~566px), so
// the buttons move under the facts line. A container query: it measures the
// patient pane, which is what actually runs out of room.
const NARROW_HEADER = "@container (max-width: 520px)";

type TabValue =
  | "patientDetails"
  | "messages"
  | "notes"
  | "documents"
  | "history"
  | "billing"
  | "aiConsultation";

interface ContactNote {
  id: string;
  content: string;
  authorName: string;
  createdAt: string;
  source?: "local" | "spruce" | "triage";
}

interface ContactMessage {
  id: string;
  spruceMessageId?: string;
  content: string;
  direction: "inbound" | "outbound";
  timestamp: string;
  status?: string;
  authorName?: string;
  authorType?: string;
  messageType?: string;
  conversationId?: string;
  attachments?: MessageAttachment[];
}

const FEE_STRUCTURE_OPTIONS: { value: FeeStructure; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "biannual", label: "Bi-Annual" },
  { value: "annual", label: "Annual" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
  { value: "not_set", label: "Not Set" },
];

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const Contacts: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [unarchiveOpen, setUnarchiveOpen] = useState(false);
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  const [unarchiveSubmitting, setUnarchiveSubmitting] = useState(false);
  const [unarchivePreview, setUnarchivePreview] = useState<any>(null);
  const [unarchiveAction, setUnarchiveAction] = useState<string>("local_only");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  // Recently opened patients — persisted so the list doesn't reset to A–Z every
  // visit. Most-recent-first, capped, deduped by id.
  const [recentPatients, setRecentPatients] = useState<Contact[]>(() => {
    try {
      const raw = localStorage.getItem('recentPatients');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  // Archived contacts are NOT shown on the normal Contacts page. They are
  // reachable only via the Admin "Spruce Contact Sync" archive icon, which opens
  // this page in a dedicated archived mode (?view=archived) in a new tab.
  const archivedView = searchParams.get("view") === "archived";
  // Baseline count of ACTIVE contacts, independent of the current search/billing
  // filter, so the footer can read "Found X of <total>" during a search.
  const [activeTotal, setActiveTotal] = useState(0);
  const [billingStatusFilter, setBillingStatusFilter] = useState<
    | "all"
    | "needs_reminder"
    | "overdue"
    | "pending"
    | "paid"
    | "zaprite"
    | "no_zaprite"
  >("all");
  const [activeTab, setActiveTab] = useState<TabValue>("patientDetails");
  const tabBodyRef = useRef<HTMLDivElement | null>(null);
  // Mobile identity lives in ONE unified 48px sticky row (back / avatar /
  // name / call / email / chart / ⋯) — the old two-row collapsing-hero
  // machinery (heroExpanded + rAF scroll handler) is gone with it.
  const stripRef = useRef<PatientClinicalStripHandle>(null);
  // Patient switch: scroll the tab body back to the top so the new patient's
  // chart opens at its start.
  useEffect(() => {
    if (tabBodyRef.current) tabBodyRef.current.scrollTop = 0;
  }, [selectedContact?.id, activeTab]);
  // Tell the app shell to hide the global mobile bottom nav while a patient is
  // open on mobile (the in-patient sub-tabs + chat composer own the bottom).
  const setImmersive = useUiChromeStore((s) => s.setImmersive);
  useEffect(() => {
    setImmersive(isMobile && !!selectedContact);
    return () => setImmersive(false);
  }, [isMobile, selectedContact, setImmersive]);
  // Record every opened patient into the recent list (covers list clicks, the
  // triage deep-link, and post-create/update selection).
  useEffect(() => {
    if (!selectedContact?.id) return;
    setRecentPatients((prev) => {
      const next = [selectedContact, ...prev.filter((c) => c.id !== selectedContact.id)].slice(0, 8);
      try {
        localStorage.setItem('recentPatients', JSON.stringify(next));
      } catch {
        /* storage full / disabled — non-fatal */
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.id]);
  // Tapping the patient name in the sticky row scrolls the chart back to top
  // (standard mobile affordance).
  const scrollTabBodyTop = useCallback(() => {
    tabBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const [syncing, setSyncing] = useState(false);
  const [zapriteSyncing, setZapriteSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalContacts, setTotalContacts] = useState(0);
  const dateTimeFormat = useAuthStore((state) => state.user?.dateTimeFormat);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [contactModalMode, setContactModalMode] = useState<"create" | "edit">(
    "edit",
  );
  const [editForm, setEditForm] = useState<Partial<Contact>>({});
  const [saving, setSaving] = useState(false);

  // Notes state
  const [notes, setNotes] = useState<ContactNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Messages state
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Mobile-only unified Chat composer: tracks whether the next send is an SMS
  // or an Internal Note. Reset per patient. Defaults to SMS, but auto-switches
  // to Note for phoneless contacts so the composer is never stuck disabled.
  const [composeMode, setComposeMode] = useState<"sms" | "note">("sms");
  useEffect(() => {
    if (!selectedContact) return;
    setComposeMode(selectedContact.phone ? "sms" : "note");
    // Only the patient identity + phone presence should retrigger this; the
    // whole `selectedContact` object reference is unstable on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.id, selectedContact?.phone]);
  // Mobile-only chat auto-follow. We never want to yank a doctor reading
  // history back to the bottom when a new inbound arrives, but we DO want
  // the latest entry visible when they're already near the bottom or just
  // sent something themselves. `forceFollowRef` is set right before our own
  // sends so the effect runs unconditionally for those.
  const prevChatLenRef = useRef(0);
  const forceFollowRef = useRef(false);
  // Unified across mobile / tablet / desktop: the outer tabBodyRef is the
  // single scroll owner for the Messages tab on every viewport, so the
  // auto-follow math (nearBottom + grew) is identical regardless of size.
  // On mobile the merged thread shows SMS + internal notes; on desktop the
  // list is SMS-only, so the tracked count differs but the behavior is the
  // same: land on the latest entry on initial load, follow when already
  // near the bottom or right after the doctor sent something, and stay put
  // when scrolled up reading history.
  const chatLen =
    activeTab === "messages"
      ? isMobile
        ? messages.length + notes.length
        : messages.length
      : 0;
  useEffect(() => {
    if (activeTab !== "messages") return;
    const el = tabBodyRef.current;
    if (!el) return;
    const prevLen = prevChatLenRef.current;
    const grew = chatLen > prevLen;
    prevChatLenRef.current = chatLen;
    if (!grew) return;
    // Initial load (prev was 0 and now we have content): land the user
    // straight on the most recent message — that's what a chat app does.
    const isInitial = prevLen === 0 && chatLen > 0;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    if (!isInitial && !nearBottom && !forceFollowRef.current) return;
    forceFollowRef.current = false;
    // Direct scrollTop is more reliable than scrollIntoView here — it
    // doesn't fight the activeTab "reset to 0" effect that ran earlier
    // in this same tick. The repeats handle images / late-mounted
    // bubbles that grow scrollHeight after the first paint, which is
    // common on slow mobile layouts and would otherwise leave the
    // user stuck a few hundred px short of the latest message.
    const jump = (smooth: boolean) => {
      const node = tabBodyRef.current;
      if (!node) return;
      if (smooth && "scrollTo" in node) {
        node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
      } else {
        // The container sets `scroll-behavior: smooth` in CSS, which turns a
        // plain `scrollTop = scrollHeight` into an animated scroll. On initial
        // load that animation is interrupted by the thread still mounting, so
        // it never reaches the bottom — the doctor lands at the top. Force an
        // instant jump by disabling smooth for this assignment only.
        const prev = node.style.scrollBehavior;
        node.style.scrollBehavior = "auto";
        node.scrollTop = node.scrollHeight;
        node.style.scrollBehavior = prev;
      }
    };
    const useSmooth = !isInitial;
    requestAnimationFrame(() => {
      jump(useSmooth);
      // Re-run after layout settles in case scrollHeight grew (avatars,
      // wrapped multi-line bubbles, etc.).
      setTimeout(() => jump(useSmooth), 60);
      setTimeout(() => jump(useSmooth), 220);
    });
  }, [chatLen, activeTab]);
  // Land on the most recent message every time the Messages tab is opened —
  // a native texting experience. The effect above only fires when the count
  // *grew*, which misses re-opening the tab (count unchanged) and competes
  // with the activeTab "reset to top" effect. This one runs on every entry,
  // forces an instant jump (bypassing the container's CSS smooth-scroll), and
  // retries so late-mounting bubbles / async message loads still end at the
  // bottom.
  useEffect(() => {
    if (activeTab !== "messages") return;
    const node = tabBodyRef.current;
    if (!node) return;
    // Land on the most recent message, native-texting style. The thread is
    // fetched + rendered asynchronously (often >1s on first open), so fixed
    // timeouts land short. Instead, pin to the bottom on every mutation while
    // the thread loads/mounts, and stop the instant the doctor scrolls up (or
    // after a hard cap) so reading history is never yanked. NOT keyed on message
    // count, so live messages don't force-scroll — the "grew" effect handles
    // follow-when-near-bottom.
    let stopped = false;
    let lastTop = -1;
    const jumpBottom = () => {
      if (stopped || !tabBodyRef.current) return;
      const n = tabBodyRef.current;
      // If the doctor has scrolled up since our last pin, stop — never yank.
      // (Checked here too, so a mutation can't re-pin after a scroll-up.)
      if (lastTop >= 0 && n.scrollTop < lastTop - 8) {
        stop();
        return;
      }
      const prev = n.style.scrollBehavior;
      n.style.scrollBehavior = "auto"; // force instant past the CSS smooth-scroll
      n.scrollTop = n.scrollHeight;
      n.style.scrollBehavior = prev;
      lastTop = n.scrollTop;
    };
    const onScroll = () => {
      // A drop below our last programmatic position = the doctor scrolled up.
      if (lastTop >= 0 && node.scrollTop < lastTop - 4) stop();
    };
    const mo = new MutationObserver(jumpBottom);
    const timer = setTimeout(stop, 6000);
    function stop() {
      if (stopped) return;
      stopped = true;
      mo.disconnect();
      node!.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    }
    jumpBottom();
    requestAnimationFrame(jumpBottom);
    mo.observe(node, { childList: true, subtree: true });
    node.addEventListener("scroll", onScroll, { passive: true });
    return stop;
  }, [activeTab, selectedContact?.id]);
  // Reset bottom-tracking when switching patients so the next conversation
  // doesn't see a stale "grew" comparison.
  useEffect(() => {
    prevChatLenRef.current = 0;
  }, [selectedContact?.id]);
  // Notes are now folded into the Messages tab on every viewport, so the
  // standalone "notes" tab no longer exists. Redirect any stale/deep-linked
  // "notes" value to the merged Messages tab so the user never lands on an
  // empty body.
  useEffect(() => {
    if (activeTab === "notes") setActiveTab("messages");
  }, [activeTab]);

  // Attachment preview state
  const [attachmentPreview, setAttachmentPreview] = useState<{
    url: string;
    filename?: string;
    contentType?: string;
    transcription?: string;
    isObjectUrl?: boolean;
  } | null>(null);
  const [attachmentPreviewText, setAttachmentPreviewText] = useState("");
  const [attachmentPreviewSource, setAttachmentPreviewSource] = useState<{
    attachment: MessageAttachment;
    message: ContactMessage;
  } | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Billing state
  const [billingForm, setBillingForm] = useState<{
    feeStructure: string;
    feeAmount: number;
    nextPaymentDue: string;
    paymentStatus: string;
  }>({ feeStructure: "", feeAmount: 0, nextPaymentDue: "", paymentStatus: "" });
  const [savingBilling, setSavingBilling] = useState(false);
  const [isBillingReminderOpen, setIsBillingReminderOpen] = useState(false);

  // Zaprite linking state
  const [isZapriteDialogOpen, setIsZapriteDialogOpen] = useState(false);
  const [zapriteTab, setZapriteTab] = useState<"import" | "upload">("import");
  const [zapriteLink, setZapriteLink] = useState<any>(null);
  const [zapriteCustomerId, setZapriteCustomerId] = useState("");
  const [zapriteLoading, setZapriteLoading] = useState(false);
  const [zapriteLinking, setZapriteLinking] = useState(false);
  const [zapriteError, setZapriteError] = useState<string | null>(null);
  const [zapriteInvoices, setZapriteInvoices] = useState<any[]>([]);
  const [zapriteInvoicesMessage, setZapriteInvoicesMessage] = useState("");
  const [zapriteInvoicesLoading, setZapriteInvoicesLoading] = useState(false);
  const [zapriteSendDialogOpen, setZapriteSendDialogOpen] = useState(false);
  const [zapriteSendOrder, setZapriteSendOrder] = useState<any | null>(null);
  const [zapriteSendMessage, setZapriteSendMessage] = useState("");
  const [zapriteSendLoading, setZapriteSendLoading] = useState(false);
  const [isZapriteEditing, setIsZapriteEditing] = useState(false);
  const [zapriteBillingDetails, setZapriteBillingDetails] = useState<
    any | null
  >(null);
  const [zapriteBillingLoading, setZapriteBillingLoading] = useState(false);
  const [zapriteBillingError, setZapriteBillingError] = useState<string | null>(
    null,
  );
  const [reminderMessage, setReminderMessage] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);

  const formatZapriteAmount = (amount?: number, currency?: string) => {
    if (amount === null || amount === undefined) return "—";
    const code = (currency || "").toUpperCase();
    if (code === "BTC" || code === "LBTC") {
      const btc = amount / 100_000_000;
      return `${btc.toFixed(8)} ${code}`;
    }
    const value = amount / 100;
    if (!code) return value.toFixed(2);
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${value.toFixed(2)} ${code}`;
    }
  };

  const formatZapriteAddress = (address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    countryCode?: string | null;
  }) => {
    if (!address) return "";
    const parts = [
      address.line1,
      address.line2,
      [address.city, address.state, address.zipCode].filter(Boolean).join(" "),
      address.countryCode,
    ]
      .map((part) => (part || "").trim())
      .filter(Boolean);
    return parts.join(", ");
  };

  const normalizeTagValue = useCallback((tag: any) => {
    if (tag === null || tag === undefined) return "";
    if (typeof tag === "string" || typeof tag === "number") return String(tag);
    if (typeof tag === "object") {
      return (
        tag.label ||
        tag.value ||
        tag.name ||
        tag.id ||
        (typeof tag.object === "string" ? tag.object : "") ||
        ""
      );
    }
    return String(tag);
  }, []);

  const normalizeTags = useCallback(
    (tags: any) => {
      if (!tags) return [];
      if (Array.isArray(tags)) {
        return tags.map(normalizeTagValue).filter(Boolean);
      }
      return [normalizeTagValue(tags)].filter(Boolean);
    },
    [normalizeTagValue],
  );

  const buildZapriteInvoiceMessage = (order: any) => {
    const invoice = order?.invoice || {};
    const invoiceNumber = invoice?.number
      ? `Invoice #${invoice.number}`
      : "invoice";
    const publicUrl = invoice?.publicUrl || "";
    const firstName =
      selectedContact?.firstName ||
      selectedContact?.fullName?.split(" ")?.[0] ||
      "";
    const greeting = firstName ? `Hi ${firstName}` : "Hi";
    return publicUrl
      ? `${greeting}, your ${invoiceNumber} is ready: ${publicUrl}`
      : `${greeting}, your ${invoiceNumber} is ready.`;
  };

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = useCallback(
    (message: string, severity: "success" | "error") => {
      setSnackbar({ open: true, message, severity });
    },
    [],
  );

  // One-click appointment booking from an AI scheduling suggestion. We open the
  // existing appointment scheduler pre-filled for the current patient and (when
  // the suggestion contained a parseable timeframe) the suggested date.
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [appointmentDraft, setAppointmentDraft] = useState<{
    date?: string;
    presentingComplaints?: string;
    aiAssisted?: boolean;
  }>({});
  // When the History timeline's "Open" action on an AI consultation marker is
  // clicked, we switch to the AI Consultation tab and ask the panel to select
  // this thread. A nonce forces re-selection even if the same thread is opened
  // twice in a row.
  const [openConsultation, setOpenConsultation] = useState<{
    threadId: string;
    nonce: number;
  } | null>(null);

  const handleOpenConsultation = useCallback((threadId: string) => {
    setOpenConsultation({ threadId, nonce: Date.now() });
    setActiveTab("aiConsultation");
  }, []);

  const appointmentInitialPatient = useMemo(() => {
    if (!selectedContact) return undefined;
    const name =
      `${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim() ||
      selectedContact.fullName ||
      "";
    return {
      name,
      phone: selectedContact.phone || "",
      email: selectedContact.email,
      internalId: selectedContact.internalId,
      spruceContactId: selectedContact.spruceContactId,
    };
  }, [selectedContact]);

  const handleCreateAppointmentFromSuggestion = useCallback(
    (suggestion: SchedulingSuggestion) => {
      setAppointmentDraft({
        date: suggestion.date,
        presentingComplaints: suggestion.text
          ? `AI scheduling suggestion: ${suggestion.text}`
          : undefined,
        aiAssisted: true,
      });
      setAppointmentModalOpen(true);
    },
    [],
  );

  // WebSocket for real-time updates
  const { socket, isConnected, on, off, emit } = useSocket();

  const mapContact = useCallback(
    (c: any): Contact => ({
      id: c.id,
      internalId: c.internal_id || c.internalId,
      spruceContactId: c.spruce_contact_id || c.spruceContactId,
      firstName: c.first_name || c.firstName,
      lastName: c.last_name || c.lastName,
      fullName:
        c.full_name ||
        c.fullName ||
        `${c.first_name || c.firstName || ""} ${c.last_name || c.lastName || ""}`.trim(),
      phone: c.phone || c.mobile_phone || c.mobilePhone,
      email: c.email,
      dateOfBirth: c.date_of_birth || c.dateOfBirth,
      gender: c.gender,
      feeStructure: c.fee_structure || c.feeStructure,
      feeAmount: c.fee_amount || c.feeAmount,
      nextPaymentDue: c.next_payment_due || c.nextPaymentDue,
      paymentStatus: c.payment_status || c.paymentStatus,
      status: c.status || "active",
      isArchived: c.is_archived ?? c.isArchived ?? false,
      tags: normalizeTags(c.tags),
      addressLine1: c.address_line1 || c.addressLine1,
      addressLine2: c.address_line2 || c.addressLine2,
      city: c.city,
      state: c.state,
      zipCode: c.zip_code || c.zipCode,
      createdAt: c.created_at || c.createdAt,
      updatedAt: c.updated_at || c.updatedAt,
      zaprite: c.zaprite,
    }),
    [normalizeTags],
  );

  // Fetch contacts from API
  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      // Active-only on the normal page; archived-only in the admin archived view.
      params.isArchived = archivedView ? "true" : "false";

      const response = await contactsApi.getContacts(params);

      if (response.data?.success && response.data?.data) {
        const mappedContacts: Contact[] = response.data.data.map(mapContact);

        setContacts(mappedContacts);
        const total =
          response.data.pagination?.total || mappedContacts.length;
        setTotalContacts(total);
        // Capture the unfiltered active baseline (no search) for the footer count.
        if (!archivedView && !searchTerm) setActiveTotal(total);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      showSnackbar("Failed to load contacts", "error");
    } finally {
      setLoading(false);
    }
  }, [searchTerm, archivedView, mapContact, showSnackbar]);

  // Deep link from the triage dashboard: router state carries the patient to
  // auto-select ({ selectInternalId }) and where "Back to task" returns
  // ({ returnTo, backLabel } → the dashboard's ?taskId= deep link reopens the
  // task dock). The state is consumed once and stripped so refresh/back don't
  // re-trigger; taskReturn survives locally to keep the button rendered.
  const location = useLocation();
  const navigate = useNavigate();
  const [taskReturn, setTaskReturn] = useState<{ to: string; label: string } | null>(null);
  const [chartDocId, setChartDocId] = useState<string | null>(null);
  const deepLinkHandledRef = useRef(false);
  // Arriving from a triage task, the user wants ONE patient — not the full
  // directory. The list endpoint returns every contact (~1,500 rows / ~2.3MB),
  // which takes seconds to download, parse and render, and that churn is what
  // made the profile feel slow. Hold it back until the deep-linked patient is
  // on screen, then load it in the background. Captured on first render,
  // before the effect below strips router state.
  const [deferList, setDeferList] = useState<boolean>(() =>
    Boolean(
      (location.state as any)?.selectInternalId ||
      new URLSearchParams(location.search).get("selectInternalId")
    ),
  );
  useEffect(() => {
    const st = (location.state as any) || {};
    const q = new URLSearchParams(location.search);
    const selectInternalId = st.selectInternalId || q.get("selectInternalId");
    const returnTo = st.returnTo || q.get("returnTo");
    const backLabel = st.backLabel || q.get("backLabel") || "Back";
    const selectDocId = st.selectDocId || q.get("selectDocId");
    const selectTab = st.selectTab || q.get("selectTab");
    if (!selectInternalId || deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true;
    if (returnTo) setTaskReturn({ to: returnTo, label: backLabel });
    if (selectDocId) setChartDocId(String(selectDocId));
    if (selectTab === "documents" || selectTab === "history" || selectTab === "patientDetails" || selectTab === "messages") {
      setActiveTab(selectTab);
    }
    (async () => {
      try {
        const response = await contactsApi.getContacts({ search: selectInternalId });
        const rows = response.data?.data || [];
        const mapped: Contact[] = rows.map(mapContact);
        const match = mapped.find((c) => c.internalId === selectInternalId) || mapped[0];
        if (match) {
          setSelectedContact(match);
        } else {
          showSnackbar(`No contact found for ${selectInternalId}`, "error");
        }
      } catch {
        showSnackbar("Failed to open the linked patient", "error");
      } finally {
        // Patient is on screen (or we failed) — let the full list load now.
        setDeferList(false);
      }
    })();
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, mapContact, navigate, showSnackbar]);

  // Fetch notes for selected contact
  const fetchNotes = useCallback(async (contactId: string) => {
    try {
      setLoadingNotes(true);
      const response = await api.get(`/contacts/${contactId}/notes`);
      if (response.data?.success) {
        setNotes(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  }, []);

  // Fetch messages for selected contact
  const fetchMessages = useCallback(async (contactId: string) => {
    try {
      setLoadingMessages(true);
      const response = await api.get(`/contacts/${contactId}/communications`);
      if (response.data?.success) {
        setMessages(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);
  const isTextAttachment = (attachment: MessageAttachment) => {
    const filename = attachment.filename?.toLowerCase() || "";
    return (
      attachment.contentType?.startsWith("text/") ||
      filename.endsWith(".txt") ||
      filename.endsWith(".md") ||
      filename.endsWith(".csv") ||
      filename.endsWith(".log") ||
      filename.endsWith(".json")
    );
  };

  const isSpruceAttachment = (
    attachment: MessageAttachment,
    message: ContactMessage,
  ) => {
    if (!message?.spruceMessageId || !attachment?.id) {
      return false;
    }
    if (attachment.source === "response") {
      return false;
    }
    if (attachment.source === "spruce") {
      return true;
    }
    if (attachment.downloadUrl || attachment.downloadToken) {
      return false;
    }
    return true;
  };

  const handlePreviewAttachment = async (
    attachment: MessageAttachment,
    message: ContactMessage,
  ) => {
    setAttachmentPreviewText("");
    setIsPreviewLoading(true);
    setAttachmentPreviewSource({ attachment, message });
    let previewUrl = attachment.downloadUrl || attachment.url || "";
    let contentType = attachment.contentType || "application/octet-stream";
    let filename = attachment.filename || "Attachment";
    let transcription = attachment.transcription;
    let isObjectUrl = false;
    let didSetPreview = false;

    try {
      const spruceMessageId = message?.spruceMessageId;
      const spruceAttachmentId = attachment.id;

      if (
        isSpruceAttachment(attachment, message) &&
        spruceMessageId &&
        spruceAttachmentId
      ) {
        const metaResponse = await triageApi.getSpruceAttachment(
          spruceMessageId,
          spruceAttachmentId,
        );
        const meta = metaResponse.data?.attachment;
        if (meta) {
          contentType = meta.contentType || contentType;
          filename = meta.filename || filename;
          transcription = meta.transcription || transcription;
        }

        const downloadResponse = await triageApi.downloadSpruceAttachment(
          spruceMessageId,
          spruceAttachmentId,
        );
        const blob = downloadResponse.data;
        previewUrl = URL.createObjectURL(blob);
        isObjectUrl = true;
        contentType = blob.type || contentType;
        if (isTextAttachment({ ...attachment, contentType, filename })) {
          const text = await blob.text();
          setAttachmentPreviewText(text);
        }
      } else if (previewUrl) {
        if (isTextAttachment({ ...attachment, contentType, filename })) {
          const textResponse = await fetch(previewUrl);
          const text = await textResponse.text();
          setAttachmentPreviewText(text);
        } else if (
          attachment.source === "response" ||
          previewUrl.includes("/api/response-attachments/download/")
        ) {
          const blobResponse = await fetch(previewUrl);
          const blob = await blobResponse.blob();
          previewUrl = URL.createObjectURL(blob);
          isObjectUrl = true;
          contentType = blob.type || contentType;
        }
      }

      if (previewUrl) {
        setAttachmentPreview({
          url: previewUrl,
          filename,
          contentType,
          transcription,
          isObjectUrl,
        });
        didSetPreview = true;
      }
    } catch (err) {
      console.error("Failed to preview attachment:", err);
    } finally {
      if (!didSetPreview && previewUrl) {
        setAttachmentPreview({
          url: previewUrl,
          filename,
          contentType,
          transcription,
          isObjectUrl: false,
        });
      }
      setIsPreviewLoading(false);
    }
  };

  const handleDownloadAttachment = async (
    attachment: MessageAttachment,
    message: ContactMessage,
  ) => {
    const downloadFallback = () => {
      const downloadUrl = attachment.downloadUrl || attachment.url;
      if (!downloadUrl) {
        return;
      }
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = attachment.filename || "attachment";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => link.remove(), 1000);
    };

    try {
      const spruceMessageId = message?.spruceMessageId;
      const spruceAttachmentId = attachment.id;

      if (
        isSpruceAttachment(attachment, message) &&
        spruceMessageId &&
        spruceAttachmentId
      ) {
        const downloadResponse = await triageApi.downloadSpruceAttachment(
          spruceMessageId,
          spruceAttachmentId,
        );
        const blob = downloadResponse.data;
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = attachment.filename || "attachment";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
          link.remove();
        }, 1000);
        return;
      }
    } catch (err) {
      console.error("Failed to download attachment:", err);
    }

    downloadFallback();
  };

  const handleClosePreview = () => {
    if (attachmentPreview?.isObjectUrl) {
      URL.revokeObjectURL(attachmentPreview.url);
    }
    setAttachmentPreview(null);
    setAttachmentPreviewText("");
    setAttachmentPreviewSource(null);
  };

  // Fetch contacts on mount and when filters change
  useEffect(() => {
    if (deferList) return; // deep-linked from a task — patient first, list after
    fetchContacts();
  }, [archivedView, fetchContacts, deferList]);

  // Debounced search
  useEffect(() => {
    if (deferList) return;
    const debounce = setTimeout(() => {
      fetchContacts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, fetchContacts, deferList]);

  // Fetch notes/messages when contact or tab changes. The Notes tab is now
  // folded into the Messages tab on every viewport, so whenever Messages is
  // active we pull BOTH SMS and notes — the composeMode toggle decides which
  // one the thread shows, but both must be loaded so switching is instant.
  useEffect(() => {
    if (!selectedContact?.id) return;
    if (activeTab === "messages") {
      fetchMessages(selectedContact.id);
      fetchNotes(selectedContact.id);
    }
  }, [selectedContact?.id, activeTab, fetchNotes, fetchMessages]);

  // Clear the previous patient's thread state when switching contacts so the
  // merged mobile Chat never momentarily renders stale notes/messages from
  // the prior patient while the new fetches are in flight.
  useEffect(() => {
    setMessages([]);
    setNotes([]);
  }, [selectedContact?.id]);

  // Update billing form when contact changes
  useEffect(() => {
    if (selectedContact) {
      setBillingForm({
        feeStructure: selectedContact.feeStructure || "",
        feeAmount: selectedContact.feeAmount || 0,
        nextPaymentDue: selectedContact.nextPaymentDue || "",
        paymentStatus: selectedContact.paymentStatus || "",
      });
    }
  }, [selectedContact]);

  // Socket event handlers for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleContactsUpdate = (data: { contacts?: Contact[] }) => {
      console.log("Socket: contacts:update received");
      if (data?.contacts) setContacts(data.contacts.map(mapContact));
    };

    const handleNewContact = (data: { contact?: Contact }) => {
      console.log("Socket: contacts:new received", data);
      if (data?.contact) {
        const mapped = mapContact(data.contact);
        setContacts((prev) => [mapped, ...prev]);
        showSnackbar(`New contact synced: ${mapped.fullName}`, "success");
      }
    };

    const handleContactUpdated = (data: { contact?: Contact }) => {
      console.log("Socket: contacts:updated received", data);
      if (data?.contact) {
        const mapped = mapContact(data.contact);
        setContacts((prev) =>
          prev.map((c) => (c.id === mapped.id ? mapped : c)),
        );
        if (selectedContact?.id === mapped.id) setSelectedContact(mapped);
      }
    };

    const handleSyncCompleted = (summary: {
      created: number;
      updated: number;
      total: number;
    }) => {
      console.log("Socket: sync:completed received", summary);
      showSnackbar(
        `Sync complete: ${summary.created} new, ${summary.updated} updated`,
        "success",
      );
      fetchContacts();
    };

    on("contacts:update", handleContactsUpdate);
    on("contacts:new", handleNewContact);
    on("contacts:updated", handleContactUpdated);
    on("sync:completed", handleSyncCompleted);

    return () => {
      off("contacts:update", handleContactsUpdate);
      off("contacts:new", handleNewContact);
      off("contacts:updated", handleContactUpdated);
      off("sync:completed", handleSyncCompleted);
    };
  }, [
    socket,
    on,
    off,
    selectedContact,
    mapContact,
    fetchContacts,
    showSnackbar,
  ]);

  // Socket for messages and notes when contact is selected
  useEffect(() => {
    if (!socket || !selectedContact) return;

    emit("contacts:subscribe", selectedContact.id);

    const handleNewMessage = (data: {
      contactId: string;
      message: ContactMessage;
    }) => {
      if (data.contactId === selectedContact.id && data.message) {
        setMessages((prev) => [...prev, data.message]);
      }
    };

    const handleNewNote = (data: { contactId: string; note: ContactNote }) => {
      if (data.contactId === selectedContact.id && data.note) {
        setNotes((prev) => [data.note, ...prev]);
      }
    };

    on("messages:new", handleNewMessage);
    on("notes:new", handleNewNote);

    return () => {
      emit("contacts:unsubscribe", selectedContact.id);
      off("messages:new", handleNewMessage);
      off("notes:new", handleNewNote);
    };
  }, [socket, selectedContact, on, off, emit]);

  const normalizeZapriteStatus = (status?: string) => {
    const normalized = (status || "").toString().trim().toLowerCase();
    if (!normalized) return "";
    if (normalized === "paid") return "paid";
    if (normalized === "sent" || normalized === "pending") return "pending";
    if (normalized === "overdue") return "overdue";
    return normalized;
  };

  const getZapriteBillingStatus = (contact: Contact) => {
    const linkId = contact.zaprite?.externalCustomerId;
    if (!linkId) return "no_link";
    const latestInvoice = contact.zaprite?.metadata?.latestInvoice;
    if (!latestInvoice) return "unknown";
    const normalized = normalizeZapriteStatus(latestInvoice.status);
    const dueDateRaw = latestInvoice.dueDate;
    if (dueDateRaw && normalized !== "paid") {
      const dueDate = new Date(`${dueDateRaw}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!isNaN(dueDate.getTime()) && dueDate < today) {
        return "overdue";
      }
    }
    if (normalized === "paid") return "paid";
    if (normalized === "overdue") return "overdue";
    if (normalized === "pending") return "pending";
    return "unknown";
  };

  const getZapriteStatusLabel = (contact: Contact) => {
    const status = getZapriteBillingStatus(contact);
    if (status === "paid") return "Paid";
    if (status === "pending") return "Unpaid";
    if (status === "overdue") return "Overdue";
    if (status === "unknown") return "Unknown";
    return "";
  };

  const getZapriteStatusTone = (status: string) => {
    if (status === "overdue") return "error.main";
    if (status === "pending") return "warning.main";
    if (status === "paid") return "success.main";
    return "text.secondary";
  };

  const filteredContacts = contacts.filter((contact) => {
    if (!archivedView && contact.isArchived) return false;
    if (archivedView && !contact.isArchived) return false;
    const zapriteLinkId = contact.zaprite?.externalCustomerId;
    const zapriteStatus = getZapriteBillingStatus(contact);
    if (billingStatusFilter === "zaprite") return Boolean(zapriteLinkId);
    if (billingStatusFilter === "no_zaprite") return !zapriteLinkId;
    if (billingStatusFilter === "needs_reminder") {
      return zapriteStatus === "overdue" || zapriteStatus === "pending";
    }
    if (billingStatusFilter === "overdue") return zapriteStatus === "overdue";
    if (billingStatusFilter === "pending") return zapriteStatus === "pending";
    if (billingStatusFilter === "paid") return zapriteStatus === "paid";
    return true;
  });

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post("/contacts/sync");
      await fetchContacts();
      showSnackbar("Sync completed successfully", "success");
    } catch (error) {
      console.error("Sync failed:", error);
      showSnackbar("Sync failed", "error");
    } finally {
      setSyncing(false);
    }
  };

  const handleZapriteSync = async () => {
    setZapriteSyncing(true);
    try {
      const response = await zapriteApi.syncAll();
      const data = response.data?.data || {};
      if (data.skipped && data.reason === "not_configured") {
        showSnackbar("Zaprite is not configured", "error");
      } else if (data.skipped && data.reason === "already_running") {
        showSnackbar("Zaprite sync is already running", "success");
      } else {
        const total = data.total ?? 0;
        const success = data.success ?? 0;
        const failed = data.failed ?? 0;
        const message = failed
          ? `Zaprite sync complete: ${success}/${total} (${failed} failed)`
          : `Zaprite sync complete: ${success}/${total}`;
        showSnackbar(message, failed ? "error" : "success");
      }
      await fetchContacts();
    } catch (error) {
      console.error("Zaprite sync failed:", error);
      showSnackbar("Zaprite sync failed", "error");
    } finally {
      setZapriteSyncing(false);
    }
  };

  const handleEditContact = () => {
    if (selectedContact) {
      setContactModalMode("edit");
      setEditForm({
        firstName: selectedContact.firstName,
        lastName: selectedContact.lastName,
        phone: selectedContact.phone,
        email: selectedContact.email,
        dateOfBirth: selectedContact.dateOfBirth,
        gender: selectedContact.gender,
        addressLine1: selectedContact.addressLine1,
        addressLine2: selectedContact.addressLine2,
        city: selectedContact.city,
        state: selectedContact.state,
        zipCode: selectedContact.zipCode,
      });
      setIsEditModalOpen(true);
    }
  };

  const handleSaveContact = async () => {
    const currentContact = selectedContact;
    const contactId = contactModalMode === "edit" ? currentContact?.id : null;
    if (contactModalMode === "edit" && !contactId) return;
    if (!editForm.firstName || !editForm.lastName) {
      showSnackbar("First name and last name are required", "error");
      return;
    }

    setSaving(true);
    try {
      if (contactModalMode === "create") {
        const createData = {
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          fullName: `${editForm.firstName} ${editForm.lastName}`.trim(),
          phone: editForm.phone,
          email: editForm.email,
          dateOfBirth: editForm.dateOfBirth,
          gender: editForm.gender,
          addressLine1: editForm.addressLine1,
          addressLine2: editForm.addressLine2,
          city: editForm.city,
          state: editForm.state,
          zipCode: editForm.zipCode,
          createdBy: "admin",
        };
        const response = await contactsApi.createContact(createData);
        const createdContact = mapContact(response.data?.data || createData);
        setContacts((prev) => [createdContact, ...prev]);
        setSelectedContact(createdContact);
        setIsEditModalOpen(false);
        setEditForm({});
        showSnackbar("Contact created successfully", "success");
      } else {
        const updateData = {
          first_name: editForm.firstName,
          last_name: editForm.lastName,
          full_name: `${editForm.firstName} ${editForm.lastName}`.trim(),
          phone: editForm.phone,
          email: editForm.email,
          date_of_birth: editForm.dateOfBirth,
          gender: editForm.gender,
          address_line1: editForm.addressLine1,
          address_line2: editForm.addressLine2,
          city: editForm.city,
          state: editForm.state,
          zip_code: editForm.zipCode,
        };

        await contactsApi.updateContact(contactId as string, updateData);

        // Update local state
        const updatedContact = {
          ...currentContact!,
          ...editForm,
          fullName: `${editForm.firstName} ${editForm.lastName}`.trim(),
        };
        setSelectedContact(updatedContact);
        setContacts((prev) =>
          prev.map((c) => (c.id === contactId ? updatedContact : c)),
        );

        setIsEditModalOpen(false);
        showSnackbar("Contact updated successfully", "success");
      }
    } catch (error) {
      console.error("Error saving contact:", error);
      showSnackbar(
        contactModalMode === "create"
          ? "Failed to create contact"
          : "Failed to update contact",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCreateContact = () => {
    setContactModalMode("create");
    setEditForm({});
    setIsEditModalOpen(true);
  };

  const closeContactModal = () => {
    setIsEditModalOpen(false);
    if (contactModalMode === "create") {
      setEditForm({});
    }
  };

  const handleSendMessage = async () => {
    if (!selectedContact?.id || !selectedContact?.phone || !newMessage.trim())
      return;

    setSendingMessage(true);
    try {
      // Use local-first approach - backend looks up conversation ID from local DB
      const response = await api.post(
        `/contacts/${selectedContact.id}/send-message`,
        {
          message: newMessage,
        },
      );

      if (response.data?.success) {
        // Add to local messages
        forceFollowRef.current = true;
        setMessages((prev) => [
          ...prev,
          {
            id: response.data.messageId || Date.now().toString(),
            content: newMessage,
            direction: "outbound",
            timestamp: new Date().toISOString(),
            status: "sent",
          },
        ]);
        setNewMessage("");
        showSnackbar("Message sent via Spruce", "success");
      } else {
        throw new Error(response.data?.error || "Failed to send");
      }
    } catch (error: any) {
      console.error("Error sending message:", error);
      showSnackbar(
        error?.response?.data?.error || "Failed to send message",
        "error",
      );
    } finally {
      setSendingMessage(false);
    }
  };

  // Mobile-only: the unified Chat composer routes the contents of `newMessage`
  // to either the SMS send path or the internal-note create path depending on
  // which pill is selected. Keeping a single text buffer means typing what
  // you intended and then toggling mode never loses the draft.
  const handleComposerSend = async () => {
    if (composeMode === "sms") {
      return handleSendMessage();
    }
    if (!selectedContact?.id || !newMessage.trim()) return;
    setSavingNote(true);
    try {
      const response = await api.post(`/contacts/${selectedContact.id}/notes`, {
        content: newMessage,
        authorName: "Admin",
        authorId: "admin",
      });
      if (response.data?.success) {
        forceFollowRef.current = true;
        setNotes((prev) => [response.data.data, ...prev]);
        setNewMessage("");
        showSnackbar("Note added", "success");
      }
    } catch (error) {
      console.error("Error adding note:", error);
      showSnackbar("Failed to add note", "error");
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveBilling = async () => {
    if (!selectedContact?.id) return;

    setSavingBilling(true);
    try {
      const updateData = {
        fee_structure: billingForm.feeStructure,
        fee_amount: billingForm.feeAmount,
        next_payment_due: billingForm.nextPaymentDue || null,
        payment_status: billingForm.paymentStatus,
      };

      await contactsApi.updateContact(selectedContact.id, updateData);

      // Update local state
      const updatedContact = {
        ...selectedContact,
        feeStructure: billingForm.feeStructure as FeeStructure,
        feeAmount: billingForm.feeAmount,
        nextPaymentDue: billingForm.nextPaymentDue,
        paymentStatus: billingForm.paymentStatus,
      };
      setSelectedContact(updatedContact);
      setContacts((prev) =>
        prev.map((c) => (c.id === selectedContact.id ? updatedContact : c)),
      );

      showSnackbar("Billing information saved", "success");
    } catch (error) {
      console.error("Error saving billing:", error);
      showSnackbar("Failed to save billing information", "error");
    } finally {
      setSavingBilling(false);
    }
  };

  const handleSendPaymentReminder = async () => {
    if (!selectedContact?.phone || !reminderMessage.trim()) return;

    setSendingReminder(true);
    try {
      const response = await api.post("/spruce-direct/send", {
        phone: selectedContact.phone,
        message: reminderMessage,
      });

      if (response.data?.success) {
        setIsBillingReminderOpen(false);
        showSnackbar("Payment reminder sent via Spruce", "success");
      } else {
        throw new Error(response.data?.error || "Failed to send");
      }
    } catch (error: any) {
      console.error("Error sending reminder:", error);
      showSnackbar(
        error?.response?.data?.error || "Failed to send reminder",
        "error",
      );
    } finally {
      setSendingReminder(false);
    }
  };

  const handleArchiveContact = () => {
    if (!selectedContact?.id) return;
    if (selectedContact.isArchived) {
      openUnarchiveFlow();
    } else {
      setArchiveConfirmOpen(true);
    }
  };

  const openUnarchiveFlow = async () => {
    if (!selectedContact?.id) return;
    setUnarchiveOpen(true);
    setUnarchivePreview(null);
    setUnarchiveAction("local_only");
    setUnarchiveLoading(true);
    try {
      const resp = await api.get(`/contacts/${selectedContact.id}/unarchive-preview`);
      const data = resp.data?.data || resp.data;
      setUnarchivePreview(data);
      if (data?.status === "linked") setUnarchiveAction("auto");
      else if (data?.status === "found" && data?.matches?.length)
        setUnarchiveAction("link:" + data.matches[0].id);
      else if (data?.status === "not_found" || data?.status === "no_identifier")
        setUnarchiveAction("create");
      else setUnarchiveAction("local_only");
    } catch (e) {
      setUnarchivePreview({ status: "lookup_error" });
      setUnarchiveAction("local_only");
    } finally {
      setUnarchiveLoading(false);
    }
  };

  const confirmUnarchive = async () => {
    if (!selectedContact?.id) return;
    setUnarchiveSubmitting(true);
    try {
      let spruceAction = unarchiveAction;
      let spruceContactId: string | undefined;
      if (unarchiveAction.startsWith("link:")) {
        spruceAction = "link";
        spruceContactId = unarchiveAction.slice(5);
      }
      const body: any = { unarchivedBy: "admin", spruceAction };
      if (spruceContactId) body.spruceContactId = spruceContactId;
      await api.post(`/contacts/${selectedContact.id}/unarchive`, body);
      showSnackbar("Contact unarchived", "success");
      await fetchContacts();
      setSelectedContact(null);
      setUnarchiveOpen(false);
    } catch (e) {
      console.error("Error unarchiving contact:", e);
      showSnackbar("Failed to unarchive contact", "error");
    } finally {
      setUnarchiveSubmitting(false);
    }
  };

  const performArchiveContact = async () => {
    if (!selectedContact?.id) return;
    setArchiving(true);
    try {
      if (selectedContact.isArchived) {
        await api.post(`/contacts/${selectedContact.id}/unarchive`, {
          unarchivedBy: "admin",
        });
        showSnackbar("Contact unarchived", "success");
      } else {
        await api.post(`/contacts/${selectedContact.id}/archive`, {
          reason: "Archived by user",
          archivedBy: "admin",
        });
        showSnackbar("Contact archived", "success");
      }
      await fetchContacts();
      setSelectedContact(null);
      setArchiveConfirmOpen(false);
    } catch (error) {
      console.error("Error archiving contact:", error);
      showSnackbar("Failed to update archive status", "error");
    } finally {
      setArchiving(false);
    }
  };

  const openPaymentReminder = () => {
    const defaultMessage = `Hi ${selectedContact?.firstName}, this is a friendly reminder that your payment is overdue. Please contact us if you have any questions. - Victor Levitan, MD.`;
    setReminderMessage(defaultMessage);
    setIsBillingReminderOpen(true);
  };

  const loadZapriteLink = useCallback(async () => {
    if (!selectedContact?.id) return;
    setZapriteLoading(true);
    setZapriteError(null);
    try {
      const response = await zapriteApi.getLink(selectedContact.id);
      const link = response.data?.data || null;
      setZapriteLink(link);
      setZapriteCustomerId(link?.external_customer_id || "");
    } catch (error) {
      setZapriteError("Failed to load Zaprite link.");
    } finally {
      setZapriteLoading(false);
    }
  }, [selectedContact?.id]);

  const loadZapriteInvoices = useCallback(async () => {
    if (!selectedContact?.id) return;
    setZapriteInvoicesLoading(true);
    setZapriteInvoicesMessage("");
    try {
      const response = await zapriteApi.getInvoices(selectedContact.id);
      setZapriteInvoices(response.data?.data || []);
      setZapriteInvoicesMessage(response.data?.message || "");
    } catch (error) {
      setZapriteInvoicesMessage("Failed to load Zaprite invoices.");
    } finally {
      setZapriteInvoicesLoading(false);
    }
  }, [selectedContact?.id]);

  const loadZapriteBillingDetails = useCallback(async () => {
    if (!selectedContact?.id) return;
    setZapriteBillingLoading(true);
    setZapriteBillingError(null);
    try {
      const response = await zapriteApi.getBillingDetails(selectedContact.id);
      setZapriteBillingDetails(response.data?.data || null);
    } catch (error) {
      setZapriteBillingDetails(null);
      setZapriteBillingError("Failed to load Zaprite billing details.");
    } finally {
      setZapriteBillingLoading(false);
    }
  }, [selectedContact?.id]);

  // Auto-sync Zaprite billing details when Billing tab opens
  useEffect(() => {
    if (activeTab !== "billing" || !selectedContact?.id) return;
    loadZapriteBillingDetails();
  }, [activeTab, selectedContact?.id, loadZapriteBillingDetails]);

  // Load Zaprite link when dialog opens or contact changes
  useEffect(() => {
    if (!selectedContact?.id) {
      setZapriteLink(null);
      setZapriteCustomerId("");
      return;
    }
    if (isZapriteDialogOpen) {
      loadZapriteLink();
    }
  }, [selectedContact?.id, isZapriteDialogOpen, loadZapriteLink]);

  // Load Zaprite invoices when linked
  useEffect(() => {
    if (!isZapriteDialogOpen || zapriteTab !== "import") return;
    if (zapriteLink?.external_customer_id) {
      loadZapriteInvoices();
    }
  }, [
    isZapriteDialogOpen,
    zapriteTab,
    zapriteLink?.external_customer_id,
    loadZapriteInvoices,
  ]);

  const openZapriteDialog = async () => {
    setZapriteError(null);
    setZapriteTab("import");
    setIsZapriteDialogOpen(true);
    await loadZapriteLink();
  };

  const handleCloseZapriteDialog = () => {
    setIsZapriteDialogOpen(false);
    setZapriteInvoices([]);
  };

  const handleZapriteLink = async () => {
    if (!selectedContact?.id || !zapriteCustomerId.trim()) return;
    setZapriteLinking(true);
    setZapriteError(null);
    try {
      const response = await zapriteApi.saveLink(selectedContact.id, {
        externalCustomerId: zapriteCustomerId.trim(),
        linkedBy: "manual",
      });
      const link = response.data?.data || null;
      setZapriteLink(link);
      setIsZapriteEditing(false);
      setZapriteCustomerId(
        link?.external_customer_id || zapriteCustomerId.trim(),
      );
      await loadZapriteBillingDetails();
      await loadZapriteInvoices();
    } catch (error) {
      const err = error as any;
      setZapriteError(
        err?.response?.data?.message || "Failed to link Zaprite customer.",
      );
    } finally {
      setZapriteLinking(false);
    }
  };

  const startZapriteEdit = () => {
    setZapriteCustomerId(zapriteLink?.external_customer_id || "");
    setIsZapriteEditing(true);
  };

  const cancelZapriteEdit = () => {
    setZapriteCustomerId(zapriteLink?.external_customer_id || "");
    setIsZapriteEditing(false);
  };

  const openZapriteSendDialog = (order: any) => {
    if (!selectedContact?.phone) {
      showSnackbar("Contact phone number is missing", "error");
      return;
    }
    setZapriteSendOrder(order);
    setZapriteSendMessage(buildZapriteInvoiceMessage(order));
    setZapriteSendDialogOpen(true);
  };

  const closeZapriteSendDialog = () => {
    if (zapriteSendLoading) return;
    setZapriteSendDialogOpen(false);
    setZapriteSendOrder(null);
    setZapriteSendMessage("");
  };

  const handleZapriteSendSms = async () => {
    if (!selectedContact?.id || !zapriteSendOrder) return;
    setZapriteSendLoading(true);
    try {
      const response = await zapriteApi.sendInvoice(
        selectedContact.id,
        zapriteSendOrder.id,
        {
          message: zapriteSendMessage,
        },
      );
      if (response.data?.success) {
        showSnackbar("Invoice sent via Spruce", "success");
        closeZapriteSendDialog();
      } else {
        throw new Error(response.data?.message || "Failed to send invoice SMS");
      }
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to send invoice SMS",
        "error",
      );
    } finally {
      setZapriteSendLoading(false);
    }
  };

  const getPaymentStatusColor = (status?: string) => {
    switch (status) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "overdue":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 0, sm: 2, md: 3 },
        flex: 1,
        // Bound the page to the viewport so the right pane's flex chain
        // (header / tab bar / scrollable body) resolves to real pixel heights.
        // Offsets = MainLayout AppBar (mt: 7 = 56px) + vertical padding
        // (p xs:1.5=24, sm:2=32, md:3=48). Keep in sync with MainLayout.
        // Immersive (mobile + patient open) hides the AppBar entirely, so
        // only the page padding is subtracted there.
        height: {
          xs:
            isMobile && selectedContact
              ? "calc(100dvh - 24px)"
              : "calc(100dvh - 80px)",
          sm: "calc(100dvh - 88px)",
          md: "calc(100dvh - 104px)",
        },
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Left Panel - Contact List */}
      <Paper
        sx={{
          width: { xs: "100%", sm: 340, md: 380, lg: 400, xl: 440 },
          flexShrink: 0,
          display:
            (!isMobile && detailsExpanded) || (isMobile && selectedContact)
              ? "none"
              : "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "nowrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Contacts
              </Typography>
              {isMobile ? (
                <Tooltip title="New contact">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleCreateContact}
                    aria-label="New contact"
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      minWidth: { xs: 44, sm: "auto" },
                      minHeight: { xs: 44, sm: "auto" },
                      "&:hover": { bgcolor: "primary.dark" },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={handleCreateContact}
                  sx={{
                    textTransform: "none",
                    px: 0.9,
                    py: 0.25,
                    minWidth: 0,
                    flexShrink: 0,
                    "& .MuiButton-startIcon": { mr: 0.4, ml: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      lineHeight: 1.05,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontSize: "0.56rem", fontWeight: 500, opacity: 0.9 }}
                    >
                      New
                    </Box>
                    <Box
                      component="span"
                      sx={{ fontSize: "0.7rem", fontWeight: 700 }}
                    >
                      Contact
                    </Box>
                  </Box>
                </Button>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
              {isMobile ? (
                <>
                  <Tooltip title={syncing ? "Syncing Spruce…" : "Sync Spruce"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleSync}
                        disabled={syncing}
                        aria-label="Sync Spruce"
                        sx={{
                          minWidth: { xs: 44, sm: "auto" },
                          minHeight: { xs: 44, sm: "auto" },
                        }}
                      >
                        {syncing ? (
                          <CircularProgress size={18} />
                        ) : (
                          <SyncIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={zapriteSyncing ? "Syncing Zaprite…" : "Sync Zaprite"}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleZapriteSync}
                        disabled={zapriteSyncing}
                        aria-label="Sync Zaprite"
                        sx={{
                          minWidth: { xs: 44, sm: "auto" },
                          minHeight: { xs: 44, sm: "auto" },
                        }}
                      >
                        {zapriteSyncing ? (
                          <CircularProgress size={18} />
                        ) : (
                          <MoneyIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      syncing ? <CircularProgress size={14} /> : <SyncIcon fontSize="small" />
                    }
                    onClick={handleSync}
                    disabled={syncing}
                    sx={{
                      textTransform: "none",
                      px: 0.6,
                      py: 0.25,
                      minWidth: 0,
                      flexShrink: 0,
                      "& .MuiButton-startIcon": { mr: 0.4, ml: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        lineHeight: 1.05,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontSize: "0.56rem", fontWeight: 500, opacity: 0.85 }}
                      >
                        Sync
                      </Box>
                      <Box
                        component="span"
                        sx={{ fontSize: "0.7rem", fontWeight: 700 }}
                      >
                        Spruce
                      </Box>
                    </Box>
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      zapriteSyncing ? (
                        <CircularProgress size={16} />
                      ) : (
                        <MoneyIcon fontSize="small" />
                      )
                    }
                    onClick={handleZapriteSync}
                    disabled={zapriteSyncing}
                    sx={{
                      textTransform: "none",
                      px: 0.6,
                      py: 0.25,
                      minWidth: 0,
                      flexShrink: 0,
                      "& .MuiButton-startIcon": { mr: 0.4, ml: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        lineHeight: 1.05,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontSize: "0.56rem", fontWeight: 500, opacity: 0.85 }}
                      >
                        Sync
                      </Box>
                      <Box
                        component="span"
                        sx={{ fontSize: "0.7rem", fontWeight: 700 }}
                      >
                        Zaprite
                      </Box>
                    </Box>
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, phone, email, ID, Zaprite ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              // One-tap clear (only shown when there's text) so users don't
              // have to hold-delete the whole query.
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    edge="end"
                    onClick={() => setSearchTerm("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              mt: 1,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel>Billing status</InputLabel>
              <Select
                value={billingStatusFilter}
                label="Billing status"
                onChange={(e) => setBillingStatusFilter(e.target.value as any)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="needs_reminder">Needs reminder</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="pending">Unpaid (Sent/Pending)</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="zaprite">Zaprite-linked only</MenuItem>
                <MenuItem value="no_zaprite">No Zaprite ID</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              size="small"
              title="Clear billing filter"
              onClick={() => setBillingStatusFilter("all")}
              disabled={billingStatusFilter === "all"}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Recently opened patients — only when not actively searching, so the
            list still filters normally once you type. */}
        {!searchTerm.trim() && recentPatients.length > 0 && (
          <Box sx={{ px: 2, pt: 1, flexShrink: 0 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
              Recent
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {recentPatients.slice(0, 5).map((c) => (
                <ButtonBase
                  key={`recent-${c.id}`}
                  onClick={() => setSelectedContact(c)}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1,
                    px: 0.5,
                    py: 0.75,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Avatar sx={{ width: 26, height: 26, fontSize: 12, bgcolor: 'primary.main', flexShrink: 0 }}>
                    {(c.firstName || c.fullName || '?').charAt(0)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {c.fullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
                      {[c.internalId, c.phone].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                  </Box>
                </ButtonBase>
              ))}
            </Box>
            <Divider sx={{ mt: 1 }} />
          </Box>
        )}

        <List
          className="scroll-momentum"
          sx={{ flex: 1, overflow: "auto", py: 0 }}
        >
          {filteredContacts.map((contact) => {
            const zapriteLinkId = contact.zaprite?.externalCustomerId;
            const zapriteStatus = getZapriteBillingStatus(contact);
            const zapriteLabel = getZapriteStatusLabel(contact);
            const isSelected = selectedContact?.id === contact.id;
            return (
              <ListItem
                key={contact.id}
                onClick={() => setSelectedContact(contact)}
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  cursor: "pointer",
                  minHeight: { xs: 64, sm: 60 },
                  position: "relative",
                  bgcolor: isSelected ? "primary.50" : "transparent",
                  "&:hover": {
                    bgcolor: isSelected ? "primary.100" : "grey.100",
                  },
                  "&:active": { bgcolor: "grey.200" },
                  // Left accent stripe for the currently selected patient —
                  // gives the master-detail layout a clear visual anchor on
                  // tablets and a stronger tap-feedback signal on mobile.
                  "&::before": isSelected
                    ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        bgcolor: "primary.main",
                      }
                    : undefined,
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    {contact.firstName?.charAt(0) || "?"}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2">
                        {contact.fullName}
                      </Typography>
                      {contact.isArchived && (
                        <Chip label="Archived" size="small" color="default" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {contact.internalId} • {contact.phone}
                      </Typography>
                      {zapriteLinkId && (
                        <Typography
                          variant="caption"
                          sx={{ color: getZapriteStatusTone(zapriteStatus) }}
                        >
                          Zaprite: {zapriteLinkId}
                          {zapriteLabel ? ` • ${zapriteLabel}` : ""}
                        </Typography>
                      )}
                      {contact.paymentStatus && (
                        <Chip
                          label={contact.paymentStatus}
                          size="small"
                          color={
                            getPaymentStatusColor(contact.paymentStatus) as any
                          }
                          sx={{
                            mt: 0.25,
                            height: 18,
                            fontSize: "0.65rem",
                            alignSelf: "flex-start",
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        <Box
          sx={{
            px: 2,
            pt: 1.25,
            // Keep the count strip clear of the iOS home indicator / Android
            // gesture bar when the contact list is the full-bleed view.
            pb: "max(env(safe-area-inset-bottom), 10px)",
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {loading
              ? "Loading..."
              : archivedView
                ? searchTerm.trim() !== "" || billingStatusFilter !== "all"
                  ? `Found ${filteredContacts.length} archived contact${
                      filteredContacts.length === 1 ? "" : "s"
                    }`
                  : `${totalContacts} archived contacts`
                : searchTerm.trim() !== "" || billingStatusFilter !== "all"
                  ? `Found ${filteredContacts.length} of ${activeTotal} contacts`
                  : `${activeTotal} contacts`}
          </Typography>
        </Box>
      </Paper>

      {/* Right Panel - Contact Details */}
      {selectedContact ? (
        <Paper
          sx={{
            flex: 1,
            display: isMobile && !selectedContact ? "none" : "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {/* Arrived from a triage task: one-tap return that reopens the
              exact task dock (dashboard ?taskId= deep link). */}
          {taskReturn && (
            <Box sx={{ px: 1, pt: 1, flexShrink: 0 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  if (String(taskReturn.to).startsWith("/m")) {
                    window.location.href = taskReturn.to;
                    return;
                  }
                  navigate(taskReturn.to);
                }}
              >
                {taskReturn.label}
              </Button>
            </Box>
          )}
          {isMobile && (
            // ONE unified 48px sticky identity row: back / avatar / name /
            // call / email / chart / ⋯ — every patient action stays reachable
            // while scrolled deep in the chart. Replaces the old sticky bar +
            // separate hero identity row (the name appeared twice).
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: (t) => t.zIndex.appBar,
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                px: 0.5,
                minHeight: 48,
                flexShrink: 0,
              }}
            >
              <Tooltip title="Back to list">
                <IconButton
                  aria-label="Back to contact list"
                  onClick={() => {
                    setDetailsExpanded(false);
                    setSelectedContact(null);
                  }}
                  sx={{ minWidth: 36, minHeight: 40, p: 0.5 }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Avatar
                aria-hidden
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "primary.main",
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                {selectedContact.firstName?.charAt(0)}
              </Avatar>
              <ButtonBase
                aria-label={`Scroll to top of ${selectedContact.fullName}'s chart`}
                onClick={scrollTabBodyTop}
                focusRipple
                sx={{
                  flex: 1,
                  minWidth: 0,
                  ml: 0.5,
                  justifyContent: "flex-start",
                  textAlign: "left",
                  borderRadius: 1,
                  px: 0.25,
                  py: 0.25,
                  "&.Mui-focusVisible": {
                    outline: (t) => `2px solid ${t.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box sx={{ minWidth: 0, width: "100%" }}>
                  <Typography
                    component="span"
                    sx={{
                      display: "block",
                      width: "100%",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      userSelect: "none",
                    }}
                  >
                    {selectedContact.fullName}
                  </Typography>
                  {/* Age · DOB · sex · ID ride under the name inside the same
                      sticky row. Age and DOB lead: they are what the doctor
                      reads the line for, and the tail is what a narrow
                      screen cuts (patientHeaderFacts). */}
                  <Typography
                    component="span"
                    sx={{
                      display: "flex",
                      width: "100%",
                      minWidth: 0,
                      fontSize: "0.66rem",
                      color: "text.secondary",
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      userSelect: "none",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {/* Tags intentionally omitted — they're in Additional Info.
                        Two spans: the lead (age, DOB) never shrinks; only the
                        tail (sex, ID) takes the ellipsis. One span cost the
                        last digit of the birth year at 375px. */}
                    {(() => {
                      const facts = patientHeaderFacts(selectedContact);
                      return (
                        <>
                          <Box component="span" sx={{ flexShrink: 0 }}>
                            {facts.lead}
                          </Box>
                          {facts.tail && (
                            <Box
                              component="span"
                              sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "pre" }}
                            >
                              {` · ${facts.tail}`}
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Typography>
                </Box>
              </ButtonBase>
              <IconButton
                aria-label="Call patient"
                disabled={!selectedContact.phone}
                {...(selectedContact.phone
                  ? { component: "a" as const, href: `tel:${selectedContact.phone}` }
                  : {})}
                sx={{ minWidth: 36, minHeight: 40, p: 0.5, color: "primary.main", "&.Mui-disabled": { color: "action.disabled" } }}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label="Email patient"
                disabled={!selectedContact.email}
                {...(selectedContact.email
                  ? { component: "a" as const, href: `mailto:${selectedContact.email}` }
                  : {})}
                sx={{ minWidth: 36, minHeight: 40, p: 0.5, color: "primary.main", "&.Mui-disabled": { color: "action.disabled" } }}
              >
                <EmailIcon fontSize="small" />
              </IconButton>
              <Tooltip title="Open Chart">
                <IconButton
                  aria-label="Open patient chart"
                  onClick={() => stripRef.current?.openChart()}
                  sx={{ minWidth: 36, minHeight: 40, p: 0.5 }}
                >
                  <DescriptionIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="More actions">
                <IconButton
                  aria-label="More patient actions"
                  onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
                  sx={{ minWidth: 36, minHeight: 40, p: 0.5 }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={headerMenuAnchor}
                open={Boolean(headerMenuAnchor)}
                onClose={() => setHeaderMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {/* "Edit Contact" intentionally omitted — the Additional Info
                    row's pencil is the single edit entry point (same modal).
                    "Expand to full view" omitted too: it toggles the desktop
                    split-pane, and the patient pane already owns the whole
                    screen here. */}
                <MenuItem
                  onClick={() => {
                    setHeaderMenuAnchor(null);
                    handleArchiveContact();
                  }}
                >
                  {selectedContact.isArchived ? (
                    <UnarchiveIcon fontSize="small" sx={{ mr: 1 }} />
                  ) : (
                    <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
                  )}
                  {selectedContact.isArchived ? "Unarchive" : "Archive"}
                </MenuItem>
              </Menu>
            </Box>
          )}
          {!isMobile && (
            <Box
              sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                borderBottom: 1,
                borderColor: "divider",
                // The header lays itself out by the width of this pane, not
                // of the window: the pane is narrow whenever the list is
                // beside it. See NARROW_HEADER.
                containerType: "inline-size",
              }}
            >
              {/* A grid, not a row: avatar | name | actions on the first
                  line, and the facts line (age, DOB…) on its own line
                  spanning name and actions. Beside the buttons it had a
                  sliver of width and wrapped one fact per line. The two
                  wrappers below are display:contents so their children
                  are the grid's items. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto minmax(0, 1fr) auto",
                  alignItems: "start",
                  columnGap: { xs: 1, sm: 2 },
                }}
              >
                <Box sx={{ display: "contents" }}>
                  <Avatar
                    sx={{
                      width: { xs: 40, sm: 56, md: 64 },
                      height: { xs: 40, sm: 56, md: 64 },
                      bgcolor: "primary.main",
                      fontSize: { xs: 18, sm: 22, md: 24 },
                      flexShrink: 0,
                      gridColumn: 1,
                      gridRow: "1 / span 3",
                    }}
                  >
                    {selectedContact.firstName?.charAt(0)}
                  </Avatar>
                  <Box sx={{ display: "contents" }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600, lineHeight: 1.2, gridColumn: 2, gridRow: 1, minWidth: 0, alignSelf: "center",
                        [NARROW_HEADER]: { gridColumn: "2 / -1" },
                      }}
                      noWrap
                    >
                      {selectedContact.fullName}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        columnGap: 1,
                        rowGap: 0.25,
                        mt: 0.5,
                        color: "text.secondary",
                        gridColumn: "2 / -1",
                        gridRow: 2,
                        minWidth: 0,
                      }}
                    >
                      {/* Age · DOB · sex · ID — the same order as the phone
                          header, from the same rule (patientHeaderFacts). The
                          header is the single identity source; the Details
                          card does not repeat name/DOB/contact info. */}
                      {(() => {
                        const facts = patientHeaderFacts(selectedContact);
                        const dot = (
                          <Typography variant="body2" color="text.disabled">
                            ·
                          </Typography>
                        );
                        return (
                          <>
                            {facts.age && (
                              <>
                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                                  {facts.age}
                                </Typography>
                                {dot}
                              </>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
                              {facts.dob}
                            </Typography>
                            {facts.gender && (
                              <>
                                {dot}
                                <Typography variant="body2" color="text.secondary">
                                  {facts.gender}
                                </Typography>
                              </>
                            )}
                            {facts.id && (
                              <>
                                {dot}
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontVariantNumeric: "tabular-nums" }}
                                  >
                                    ID {facts.id}
                                  </Typography>
                                  <Tooltip title="Copy patient ID">
                                    <IconButton
                                      size="small"
                                      aria-label="Copy patient ID"
                                      onClick={() => {
                                        navigator.clipboard?.writeText(String(facts.id));
                                        showSnackbar("Patient ID copied", "success");
                                      }}
                                      sx={{
                                        p: 0.25,
                                        color: "text.disabled",
                                        "&:hover": { color: "text.secondary" },
                                      }}
                                    >
                                      <CopyIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </>
                            )}
                          </>
                        );
                      })()}
                      {/* Tags ride on the meta line (after Age · DOB · Sex · ID) so
                            they don't cost a second row of vertical space. */}
                      {selectedContact.tags?.slice(0, 4).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: "0.68rem",
                            maxWidth: 220,
                            "& .MuiChip-label": {
                              px: 0.6,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      ))}
                    </Box>
                    {(selectedContact.isArchived ||
                      selectedContact.paymentStatus) && (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5, gridColumn: "2 / -1", gridRow: 3 }}
                      >
                        {selectedContact.isArchived && (
                          <Chip
                            label="Archived"
                            size="small"
                            color="default"
                            sx={{ height: 22, fontSize: "0.7rem" }}
                          />
                        )}
                        {selectedContact.paymentStatus === "overdue" && (
                          <Chip
                            label="Payment Overdue"
                            size="small"
                            color="error"
                            sx={{ height: 22, fontSize: "0.7rem" }}
                          />
                        )}
                        {selectedContact.paymentStatus === "pending" && (
                          <Chip
                            label="Payment Pending"
                            size="small"
                            color="warning"
                            sx={{ height: 22, fontSize: "0.7rem" }}
                          />
                        )}
                        {selectedContact.paymentStatus === "paid" && (
                          <Chip
                            label="Paid"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.7rem" }}
                          />
                        )}
                      </Stack>
                    )}
                    {isMobile &&
                      (selectedContact.phone || selectedContact.email) && (
                        <Box
                          sx={{
                            mt: 1.25,
                            display: "inline-flex",
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                            "& .MuiIconButton-root": { borderRadius: 0 },
                            "& .MuiIconButton-root + .MuiIconButton-root": {
                              borderLeft: 1,
                              borderColor: "divider",
                            },
                          }}
                        >
                          {selectedContact.phone && (
                            <>
                              <Tooltip title="Call">
                                <IconButton
                                  size="small"
                                  aria-label={`Call ${selectedContact.fullName}`}
                                  component="a"
                                  href={`tel:${selectedContact.phone}`}
                                >
                                  <PhoneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open Messages">
                                <IconButton
                                  size="small"
                                  aria-label={`Open messages with ${selectedContact.fullName}`}
                                  onClick={() => {
                                    setActiveTab("messages");
                                    setComposeMode("sms");
                                  }}
                                >
                                  <SmsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {selectedContact.email && (
                            <Tooltip title="Send Email">
                              <IconButton
                                size="small"
                                aria-label={`Email ${selectedContact.fullName}`}
                                component="a"
                                href={`mailto:${selectedContact.email}`}
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    alignItems: "center",
                    flexShrink: 0,
                    gridColumn: 3,
                    gridRow: 1,
                    // Narrow pane: the buttons take their own row under the
                    // facts. Beside the name they took the whole row and the
                    // patient's name was left with no width at all.
                    [NARROW_HEADER]: {
                      gridColumn: "1 / -1",
                      gridRow: 4,
                      flexWrap: "wrap",
                      justifyContent: "flex-start",
                      mt: 1,
                    },
                  }}
                >
                  {!isMobile && (
                    <Chip
                      icon={
                        isConnected ? (
                          <WifiIcon fontSize="small" />
                        ) : (
                          <WifiOffIcon fontSize="small" />
                        )
                      }
                      label={isConnected ? "Live" : "Offline"}
                      color={isConnected ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {isMobile ? (
                    <>
                      <Tooltip title="More actions">
                        <IconButton
                          aria-label="More patient actions"
                          onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={headerMenuAnchor}
                        open={Boolean(headerMenuAnchor)}
                        onClose={() => setHeaderMenuAnchor(null)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        {/* Edit Contact omitted — the Additional Info row's
                            pencil is the single edit entry point. */}
                        <MenuItem
                          onClick={() => {
                            setHeaderMenuAnchor(null);
                            handleArchiveContact();
                          }}
                        >
                          {selectedContact.isArchived ? (
                            <UnarchiveIcon fontSize="small" sx={{ mr: 1 }} />
                          ) : (
                            <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
                          )}
                          {selectedContact.isArchived ? "Unarchive" : "Archive"}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setHeaderMenuAnchor(null);
                            setDetailsExpanded((v) => !v);
                          }}
                        >
                          {detailsExpanded ? (
                            <FullscreenExitIcon
                              fontSize="small"
                              sx={{ mr: 1 }}
                            />
                          ) : (
                            <FullscreenIcon fontSize="small" sx={{ mr: 1 }} />
                          )}
                          {detailsExpanded
                            ? "Exit full view"
                            : "Expand to full view"}
                        </MenuItem>
                      </Menu>
                    </>
                  ) : (
                    <>
                      {(selectedContact.phone || selectedContact.email) && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden",
                            mr: 1,
                            "& .MuiIconButton-root": {
                              borderRadius: 0,
                              px: 1.25,
                            },
                            "& .MuiIconButton-root + .MuiIconButton-root": {
                              borderLeft: 1,
                              borderColor: "divider",
                            },
                          }}
                        >
                          {selectedContact.phone && (
                            <>
                              <Tooltip title="Call">
                                <IconButton
                                  size="small"
                                  aria-label={`Call ${selectedContact.fullName}`}
                                  component="a"
                                  href={`tel:${selectedContact.phone}`}
                                >
                                  <PhoneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open Messages">
                                <IconButton
                                  size="small"
                                  aria-label={`Open messages with ${selectedContact.fullName}`}
                                  onClick={() => {
                                    setActiveTab("messages");
                                    setComposeMode("sms");
                                  }}
                                >
                                  <SmsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {selectedContact.email && (
                            <Tooltip title="Send Email">
                              <IconButton
                                size="small"
                                aria-label={`Email ${selectedContact.fullName}`}
                                component="a"
                                href={`mailto:${selectedContact.email}`}
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      {/* Desktop twin of the mobile chart icon: opens the same
                          chart sheet (right-side panel on desktop) from any tab. */}
                      <Tooltip title="Open Chart">
                        <IconButton
                          aria-label="Open patient chart"
                          onClick={() => stripRef.current?.openChart()}
                        >
                          <DescriptionIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Edit icon intentionally omitted — the Additional Info
                          row's pencil is the single edit entry point (same
                          modal), matching mobile. */}
                      <Tooltip
                        title={
                          selectedContact.isArchived ? "Unarchive" : "Archive"
                        }
                      >
                        <IconButton
                          onClick={handleArchiveContact}
                          aria-label={
                            selectedContact.isArchived
                              ? "Unarchive contact"
                              : "Archive contact"
                          }
                        >
                          {selectedContact.isArchived ? (
                            <UnarchiveIcon />
                          ) : (
                            <ArchiveIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          detailsExpanded
                            ? "Exit full view"
                            : "Expand to full view"
                        }
                      >
                        <IconButton
                          onClick={() => setDetailsExpanded((v) => !v)}
                          aria-label={
                            detailsExpanded
                              ? "Exit full view"
                              : "Expand to full view"
                          }
                        >
                          {detailsExpanded ? (
                            <FullscreenExitIcon />
                          ) : (
                            <FullscreenIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Desktop pill strip removed. Chart access is the single "Open
              Chart" icon in the header → opens the SAME chart sheet the mobile
              hero uses, here as a right-side panel (chartSurface). One control,
              one behavior, both platforms. This hidden mount hosts that sheet +
              its section editors (all portal-rendered). */}
          {!isMobile && (
            <PatientClinicalStrip
              ref={stripRef}
              chartSurface
              spruceEntityId={
                selectedContact?.internalId ||
                selectedContact?.spruceContactId ||
                selectedContact?.id
              }
              resetSignal={`${selectedContact?.id || ""}::${activeTab}`}
              patientName={`${selectedContact?.firstName || ""} ${selectedContact?.lastName || ""}`.trim()}
              patientEmail={selectedContact?.email}
              patientPhone={selectedContact?.phone}
            />
          )}

          {!isMobile && (
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant={isMobile || isTablet ? "scrollable" : "standard"}
                scrollButtons={isMobile || isTablet ? "auto" : false}
                allowScrollButtonsMobile
                sx={{
                  // Density pass: 40/44 tab heights, tighter horizontal padding.
                  minHeight: { xs: 40, sm: 44 },
                  "& .MuiTab-root": {
                    minHeight: { xs: 40, sm: 44 },
                    minWidth: { xs: "auto", sm: 72 },
                    px: { xs: 1, sm: 1.5 },
                  },
                }}
              >
                <Tab
                  icon={<DetailsIcon />}
                  iconPosition={isMobile ? "top" : "start"}
                  label="Details"
                  value="patientDetails"
                  aria-label="Patient Details"
                />
                <Tab
                  icon={<AiIcon />}
                  iconPosition={isMobile ? "top" : "start"}
                  label="AI"
                  value="aiConsultation"
                  aria-label="AI Consultation"
                />
                <Tab
                  icon={<MessageIcon />}
                  iconPosition={isMobile ? "top" : "start"}
                  label={isMobile ? "Chat" : "Messages"}
                  value="messages"
                  aria-label="Messages"
                />
                <Tab
                  icon={<DocumentsTabIcon />}
                  iconPosition={isMobile ? "top" : "start"}
                  label="Documents"
                  value="documents"
                  aria-label="Documents"
                />
                <Tab
                  icon={<BillingIcon />}
                  iconPosition={isMobile ? "top" : "start"}
                  label="Billing"
                  value="billing"
                  aria-label="Billing"
                />
                <Tab
                  icon={<HistoryIcon />}
                  iconPosition={isMobile ? "top" : "start"}
                  label="History"
                  value="history"
                  aria-label="History"
                />
              </Tabs>
            </Box>
          )}

          <Box
            ref={tabBodyRef}
            className="scroll-momentum"
            sx={{
              flex: 1,
              // The AI Consultation panel owns its own scrolling on desktop
              // (independent thread-list and chat panes), so the outer tab body
              // must NOT also scroll — otherwise two scrollbars stack at the
              // right edge. Every other tab keeps the normal single page scroll.
              overflow:
                activeTab === "aiConsultation" && !isMobile ? "hidden" : "auto",
              minHeight: 0,
              p: 0,
              // No scroll-snap here. We tried `y proximity` to make the
              // hero / tab-strip catch cleanly, but inside the Chart tab
              // the snap fires while the doctor is reading expanded
              // accordion content and yanks the viewport — reads as
              // sections closing. The sticky tab strip below already
              // keeps Info/Chart/Chat/Billing visible at all times, so
              // snap was just polish that's now causing real friction.
              // `overscrollBehavior: contain` still prevents the page
              // chrome from bouncing when the inner scroll bottoms out.
              // Applied on every viewport so the Messages tab feels the
              // same on desktop / tablet / mobile — single scroll owner,
              // momentum where the OS provides it, no chained scroll into
              // the surrounding workspace iframe.
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
              scrollBehavior: "smooth",
            }}
          >
            {isMobile && (
              <>
                {/* ID · sex · age · tags all live in the sticky row's meta
                    line now. Only STATUS chips (archived / payment) remain
                    here, rendered only when present so no empty padding is
                    left. Scrolls away with the content. */}
                {(selectedContact.isArchived ||
                  selectedContact.paymentStatus === "overdue" ||
                  selectedContact.paymentStatus === "pending" ||
                  selectedContact.paymentStatus === "paid") && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      columnGap: 0.5,
                      rowGap: 0.5,
                      px: 1,
                      pt: 0.5,
                      pb: 0.25,
                    }}
                  >
                    {selectedContact.isArchived && (
                      <Chip
                        label="Archived"
                        size="small"
                        sx={{ height: 18, fontSize: "0.62rem" }}
                      />
                    )}
                    {selectedContact.paymentStatus === "overdue" && (
                      <Chip
                        label="Payment Overdue"
                        size="small"
                        color="error"
                        sx={{ height: 18, fontSize: "0.62rem" }}
                      />
                    )}
                    {selectedContact.paymentStatus === "pending" && (
                      <Chip
                        label="Payment Pending"
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: "0.62rem" }}
                      />
                    )}
                    {selectedContact.paymentStatus === "paid" && (
                      <Chip
                        label="Paid"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.62rem" }}
                      />
                    )}
                  </Box>
                )}
                {/* Chart strip: trigger bar hidden (the sticky row's chart
                      icon opens the sheet via stripRef) — this mount hosts
                      the bottom sheet + inline expanded section panels. */}
                <Box sx={{ width: "100%" }}>
                  <PatientClinicalStrip
                    ref={stripRef}
                    hideMobileTrigger
                    spruceEntityId={
                      selectedContact?.internalId ||
                      selectedContact?.spruceContactId ||
                      selectedContact?.id
                    }
                    resetSignal={`${selectedContact?.id || ""}::${activeTab}`}
                    patientName={`${selectedContact?.firstName || ""} ${selectedContact?.lastName || ""}`.trim()}
                    patientEmail={selectedContact?.email}
                    patientPhone={selectedContact?.phone}
                  />
                </Box>
                {/* Sticky tab strip — pins right under the compact bar so
                    Info/Chart/Chat/Billing never disappears as content
                    scrolls. No scroll-snap: the doctor stays in full
                    control of where the viewport rests inside expanded
                    chart content. */}
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  {/* Icon-only tabs (density pass: labels dropped, aria-labels
                      kept). fullWidth: 6 icons fit 375px without scrolling. */}
                  <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="fullWidth"
                    sx={{
                      minHeight: 40,
                      "& .MuiTab-root": {
                        minHeight: 40,
                        minWidth: "auto",
                        px: 0.5,
                      },
                    }}
                  >
                    <Tab
                      icon={<DetailsIcon />}
                      value="patientDetails"
                      aria-label="Patient Details"
                    />
                    <Tab
                      icon={<AiIcon />}
                      value="aiConsultation"
                      aria-label="AI Consultation"
                    />
                    <Tab
                      icon={<MessageIcon />}
                      value="messages"
                      aria-label="Messages"
                    />
                    <Tab
                      icon={<DocumentsTabIcon />}
                      value="documents"
                      aria-label="Documents"
                    />
                    <Tab
                      icon={<BillingIcon />}
                      value="billing"
                      aria-label="Billing"
                    />
                    <Tab
                      icon={<HistoryIcon />}
                      value="history"
                      aria-label="History"
                    />
                  </Tabs>
                </Box>
              </>
            )}
            <Box
              sx={{
                // The AI Consultation tab owns its own scrolling: the thread list
                // and the chat transcript each scroll independently while the
                // composer stays pinned. So on desktop we hand the panel the full
                // tab-body height (no padding, non-scrolling flex column) instead
                // of letting the whole tab body scroll as one. Other tabs keep the
                // normal padded, page-scrolling layout.
                ...(activeTab === "aiConsultation" && !isMobile
                  ? {
                      p: 0,
                      height: "100%",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }
                  : {
                      p: { xs: 1, sm: 1.5, md: 2 },
                      // Reserve room for the home indicator so the bottom of any
                      // tab (Notes save button, Billing rows, etc.) never sits
                      // under it.
                      pb: {
                        xs: "max(env(safe-area-inset-bottom), 8px)",
                        sm: 1.5,
                        md: 2,
                      },
                    }),
              }}
            >
              {/* Patient Details Tab */}
              {activeTab === "patientDetails" && (
                  <PatientDetailsView
                    view="details"
                    spruceEntityId={
                      selectedContact?.internalId ||
                      selectedContact?.spruceContactId ||
                      selectedContact?.id
                    }
                    patientName={`${selectedContact?.firstName || ""} ${selectedContact?.lastName || ""}`.trim()}
                    patientPhone={selectedContact?.phone}
                    patientEmail={selectedContact?.email}
                    patientDob={selectedContact?.dateOfBirth}
                    patientGender={selectedContact?.gender}
                    contact={selectedContact}
                    onEditContact={handleEditContact}
                    onOpenMessages={() => {
                      setActiveTab("messages");
                      setComposeMode("sms");
                    }}
                    onOpenConsultation={handleOpenConsultation}
                    onOpenHistory={() => setActiveTab("history")}
                  />
              )}

              {/* Documents Tab — the Consolidated Summary + Documents module,
                  relocated here from the Patient Details tab. */}
              {activeTab === "documents" && (
                  <PatientDetailsView
                    view="documents"
                    initialDocId={chartDocId || undefined}
                    spruceEntityId={
                      selectedContact?.internalId ||
                      selectedContact?.spruceContactId ||
                      selectedContact?.id
                    }
                    patientName={`${selectedContact?.firstName || ""} ${selectedContact?.lastName || ""}`.trim()}
                    patientPhone={selectedContact?.phone}
                    patientEmail={selectedContact?.email}
                    patientDob={selectedContact?.dateOfBirth}
                    patientGender={selectedContact?.gender}
                    contact={selectedContact}
                    onEditContact={handleEditContact}
                    onOpenMessages={() => {
                      setActiveTab("messages");
                      setComposeMode("sms");
                    }}
                    onOpenConsultation={handleOpenConsultation}
                    onOpenHistory={() => setActiveTab("history")}
                  />
              )}

              {/* History Tab — full patient-journey timeline, relocated out of
                  the Patient Details tab. Practice-Fusion-style category filters. */}
              {activeTab === "history" && (
                  <PatientDetailsView
                    view="history"
                    spruceEntityId={
                      selectedContact?.internalId ||
                      selectedContact?.spruceContactId ||
                      selectedContact?.id
                    }
                    patientName={`${selectedContact?.firstName || ""} ${selectedContact?.lastName || ""}`.trim()}
                    patientPhone={selectedContact?.phone}
                    patientEmail={selectedContact?.email}
                    patientDob={selectedContact?.dateOfBirth}
                    patientGender={selectedContact?.gender}
                    contact={selectedContact}
                    onEditContact={handleEditContact}
                    onOpenMessages={() => {
                      setActiveTab("messages");
                      setComposeMode("sms");
                    }}
                    onOpenConsultation={handleOpenConsultation}
                    onOpenHistory={() => setActiveTab("history")}
                  />
              )}

              {/* AI Consultation Tab */}
              {activeTab === "aiConsultation" && (
                <AIConsultationPanel
                  spruceEntityId={
                    selectedContact?.internalId ||
                    selectedContact?.spruceContactId ||
                    selectedContact?.id
                  }
                  patientName={`${selectedContact?.firstName || ""} ${selectedContact?.lastName || ""}`.trim()}
                  onCreateAppointment={
                    selectedContact
                      ? handleCreateAppointmentFromSuggestion
                      : undefined
                  }
                  openThreadId={openConsultation?.threadId}
                  openThreadNonce={openConsultation?.nonce}
                />
              )}

              {/* Messages Tab */}
              {activeTab === "messages" &&
                (() => {
                  // Build the merged Chat timeline (SMS + Internal Notes) for the
                  // mobile-only unified thread. Sorted ascending so the latest
                  // entry sits at the bottom next to the composer, matching the
                  // existing desktop Messages list and standard chat UX.
                  type ChatItem =
                    | { kind: "message"; ts: number; data: ContactMessage }
                    | { kind: "note"; ts: number; data: ContactNote };
                  // The composer mode also acts as a thread filter on mobile:
                  // selecting "Note" hides SMS so the doctor sees just the
                  // internal-note timeline, and vice versa. Keeps the active
                  // context obvious — what you compose is what you see.
                  const chatItems: ChatItem[] = isMobile
                    ? [
                        ...(composeMode === "note"
                          ? []
                          : messages
                        ).map<ChatItem>((m) => ({
                          kind: "message",
                          ts: new Date(m.timestamp || 0).getTime() || 0,
                          data: m,
                        })),
                        ...(composeMode === "sms" ? [] : notes).map<ChatItem>(
                          (n) => ({
                            kind: "note",
                            ts: new Date(n.createdAt || 0).getTime() || 0,
                            data: n,
                          }),
                        ),
                      ].sort((a, b) => a.ts - b.ts)
                    : [];
                  const dayKey = (ts: number) => {
                    const d = new Date(ts);
                    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                  };
                  const dayLabel = (ts: number) => {
                    const d = new Date(ts);
                    return d.toLocaleDateString(undefined, {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    });
                  };
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minHeight: "100%",
                      }}
                    >
                      <Box
                        // Single scroll owner on every viewport: the outer
                        // `tabBodyRef` scrolls the entire patient pane (hero +
                        // tabs + thread + composer). We don't nest a second
                        // scroll container here, so auto-follow detection
                        // (nearBottom on tabBodyRef) stays consistent and we
                        // never trap momentum inside a nested div. Bottom
                        // padding clears the sticky composer so the last
                        // bubble is never hidden behind it.
                        sx={{
                          flex: 1,
                          overflowY: "visible",
                          overflowX: "hidden",
                          mx: { xs: -1, sm: 0 },
                          pb: "calc(88px + env(safe-area-inset-bottom))",
                        }}
                      >
                        {loadingMessages || (isMobile && loadingNotes) ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              py: 4,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : isMobile ? (
                          chatItems.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ textAlign: "center", py: 4 }}
                            >
                              No messages or notes yet. Use the composer below
                              to start.
                            </Typography>
                          ) : (
                            <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                              {chatItems.map((item, idx) => {
                                const prev =
                                  idx > 0 ? chatItems[idx - 1] : null;
                                const next =
                                  idx < chatItems.length - 1
                                    ? chatItems[idx + 1]
                                    : null;
                                const showDay =
                                  !prev || dayKey(prev.ts) !== dayKey(item.ts);
                                // iMessage-style bubble grouping. Consecutive bubbles
                                // from the same sender within ~5 min collapse into a
                                // single visual group: only the first shows the
                                // author label, only the last shows the timestamp,
                                // and the tail-corner stays sharp on the last bubble
                                // for the classic "speech tail" look.
                                const FIVE_MIN = 5 * 60 * 1000;
                                const sameDirectionAsPrev =
                                  !!prev &&
                                  prev.kind === item.kind &&
                                  (item.kind === "message" &&
                                  prev.kind === "message"
                                    ? prev.data.direction ===
                                      item.data.direction
                                    : true);
                                const sameDirectionAsNext =
                                  !!next &&
                                  next.kind === item.kind &&
                                  (item.kind === "message" &&
                                  next.kind === "message"
                                    ? next.data.direction ===
                                      item.data.direction
                                    : true);
                                const groupedWithPrev =
                                  sameDirectionAsPrev &&
                                  !showDay &&
                                  item.ts - (prev?.ts || 0) < FIVE_MIN;
                                const groupedWithNext =
                                  sameDirectionAsNext &&
                                  !!next &&
                                  dayKey(next.ts) === dayKey(item.ts) &&
                                  next.ts - item.ts < FIVE_MIN;
                                const isFirstInGroup = !groupedWithPrev;
                                const isLastInGroup = !groupedWithNext;
                                return (
                                  <React.Fragment
                                    key={`${item.kind}-${item.data.id || idx}`}
                                  >
                                    {showDay && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                          mt: idx === 0 ? 0.5 : 2,
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: "0.7rem",
                                            color: "text.disabled",
                                            fontWeight: 600,
                                            letterSpacing: 0.3,
                                            textTransform: "uppercase",
                                          }}
                                        >
                                          {dayLabel(item.ts)}
                                        </Typography>
                                      </Box>
                                    )}
                                    {item.kind === "note" ? (
                                      // Full-width amber Internal Note banner. Visually
                                      // distinct from SMS bubbles so a doctor can tell
                                      // at a glance whether something went to the
                                      // patient or stayed inside the practice.
                                      <Box
                                        sx={{
                                          mt: isFirstInGroup ? 0.75 : 0.125,
                                          mb: isLastInGroup ? 0.75 : 0.125,
                                          px: 1.25,
                                          py: 1,
                                          bgcolor: "warning.50",
                                          borderLeft: 3,
                                          borderColor: "warning.main",
                                          borderRadius: 1,
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.25,
                                        }}
                                      >
                                        {isFirstInGroup && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              gap: 1,
                                              alignItems: "center",
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontWeight: 700,
                                                color: "warning.dark",
                                                textTransform: "uppercase",
                                                letterSpacing: 0.5,
                                                fontSize: "0.65rem",
                                              }}
                                            >
                                              Internal Note ·{" "}
                                              {item.data.authorName || "Staff"}
                                            </Typography>
                                          </Box>
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            color: "warning.dark",
                                          }}
                                        >
                                          {item.data.content}
                                        </Typography>
                                        {isLastInGroup &&
                                          item.data.createdAt && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: "0.65rem",
                                                color: "warning.dark",
                                                opacity: 0.7,
                                                alignSelf: "flex-end",
                                              }}
                                            >
                                              {formatDateTime(
                                                item.data.createdAt,
                                                dateTimeFormat,
                                              )}
                                            </Typography>
                                          )}
                                      </Box>
                                    ) : (
                                      (() => {
                                        const isOutbound =
                                          item.data.direction === "outbound";
                                        // iMessage tail: every bubble is a fully
                                        // rounded pill; only the LAST bubble in a
                                        // group gets a sharpened tail corner on the
                                        // sender's side. Intermediate bubbles stay
                                        // fully rounded so the stack reads as a
                                        // continuous shape.
                                        const TIGHT = 6;
                                        const ROUND = 18;
                                        const topLeft = ROUND;
                                        const topRight = ROUND;
                                        const bottomLeft =
                                          !isOutbound && isLastInGroup
                                            ? TIGHT
                                            : ROUND;
                                        const bottomRight =
                                          isOutbound && isLastInGroup
                                            ? TIGHT
                                            : ROUND;
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              alignItems: isOutbound
                                                ? "flex-end"
                                                : "flex-start",
                                              mt: isFirstInGroup ? 1 : 0.125,
                                              mb: isLastInGroup ? 0.25 : 0.125,
                                            }}
                                          >
                                            {isFirstInGroup &&
                                              !isOutbound &&
                                              item.data.authorName && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{
                                                    mb: 0.25,
                                                    ml: 1.5,
                                                    fontSize: "0.7rem",
                                                  }}
                                                >
                                                  {item.data.authorName}
                                                </Typography>
                                              )}
                                            <Box
                                              sx={{
                                                px: 1.5,
                                                py: 0.875,
                                                maxWidth: "78%",
                                                bgcolor: isOutbound
                                                  ? "primary.main"
                                                  : "grey.100",
                                                color: isOutbound
                                                  ? "primary.contrastText"
                                                  : "text.primary",
                                                borderTopLeftRadius: topLeft,
                                                borderTopRightRadius: topRight,
                                                borderBottomLeftRadius:
                                                  bottomLeft,
                                                borderBottomRightRadius:
                                                  bottomRight,
                                                // Subtle elevation only on outbound for
                                                // depth without busy shadows everywhere.
                                                boxShadow: isOutbound
                                                  ? "0 1px 1px rgba(0,0,0,0.08)"
                                                  : "none",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  whiteSpace: "pre-wrap",
                                                  wordBreak: "break-word",
                                                  lineHeight: 1.35,
                                                }}
                                              >
                                                {item.data.content}
                                              </Typography>
                                              {item.data.attachments &&
                                                item.data.attachments.length >
                                                  0 && (
                                                  <Box
                                                    sx={{
                                                      mt: 0.75,
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      gap: 0.5,
                                                    }}
                                                  >
                                                    {item.data.attachments.map(
                                                      (
                                                        attachment: MessageAttachment,
                                                        ai: number,
                                                      ) => (
                                                        <Box
                                                          key={`${attachment.id || attachment.filename || ai}`}
                                                          sx={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            gap: 0.5,
                                                            flexWrap: "wrap",
                                                          }}
                                                        >
                                                          <AttachIcon fontSize="small" />
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              fontWeight: 500,
                                                            }}
                                                          >
                                                            {attachment.filename ||
                                                              "Attachment"}
                                                          </Typography>
                                                          <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() =>
                                                              handlePreviewAttachment(
                                                                attachment,
                                                                item.data as ContactMessage,
                                                              )
                                                            }
                                                            sx={{
                                                              minWidth: 0,
                                                              px: 0.5,
                                                              color: isOutbound
                                                                ? "primary.contrastText"
                                                                : "primary.main",
                                                              textDecoration:
                                                                "underline",
                                                            }}
                                                          >
                                                            View
                                                          </Button>
                                                        </Box>
                                                      ),
                                                    )}
                                                  </Box>
                                                )}
                                            </Box>
                                            {isLastInGroup && (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                  mt: 0.25,
                                                  mx: 1,
                                                  fontSize: "0.68rem",
                                                }}
                                              >
                                                {item.data.timestamp &&
                                                  new Date(
                                                    item.data.timestamp,
                                                  ).toLocaleTimeString(
                                                    undefined,
                                                    {
                                                      hour: "numeric",
                                                      minute: "2-digit",
                                                    },
                                                  )}
                                                {isOutbound &&
                                                  item.data.status &&
                                                  ` · ${item.data.status === "sent" ? "Sent" : item.data.status === "delivered" ? "Delivered" : item.data.status === "read" ? "Read" : item.data.status === "failed" ? "Failed" : item.data.status}`}
                                              </Typography>
                                            )}
                                          </Box>
                                        );
                                      })()
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </Box>
                          )
                        ) : composeMode === "note" ? (
                          // Note mode (desktop): the toggle filters the thread to
                          // the internal-notes timeline. Same rich rendering the
                          // standalone Notes tab used, now folded in here.
                          loadingNotes ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 4,
                              }}
                            >
                              <CircularProgress />
                            </Box>
                          ) : notes.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ textAlign: "center", py: 4 }}
                            >
                              No internal notes yet. Add one with the composer
                              below — notes are staff-only and never sent to the
                              patient.
                            </Typography>
                          ) : (
                            <List>
                              {notes.map((note, idx) => (
                                <ListItem
                                  key={note.id || idx}
                                  sx={{
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    bgcolor:
                                      note.source === "spruce"
                                        ? "info.50"
                                        : note.source === "triage"
                                          ? "warning.50"
                                          : "grey.50",
                                    mb: 1,
                                    borderRadius: 1,
                                    borderLeft: "3px solid",
                                    borderColor:
                                      note.source === "spruce"
                                        ? "info.main"
                                        : note.source === "triage"
                                          ? "warning.main"
                                          : "warning.light",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      width: "100%",
                                    }}
                                  >
                                    {note.source === "spruce" ? (
                                      <Chip
                                        label="Spruce"
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        sx={{ fontSize: "0.65rem", height: 18 }}
                                      />
                                    ) : note.source === "triage" ? (
                                      <Chip
                                        label="Triage"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ fontSize: "0.65rem", height: 18 }}
                                      />
                                    ) : (
                                      <Chip
                                        label="Internal"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ fontSize: "0.65rem", height: 18 }}
                                      />
                                    )}
                                    <Typography
                                      variant="body2"
                                      sx={{ flex: 1 }}
                                    >
                                      {note.content}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 1 }}
                                  >
                                    {note.authorName} •{" "}
                                    {note.createdAt &&
                                      formatDateTime(
                                        note.createdAt,
                                        dateTimeFormat,
                                      )}
                                  </Typography>
                                </ListItem>
                              ))}
                            </List>
                          )
                        ) : messages.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: "center", py: 4 }}
                          >
                            No messages yet. Send your first message below.
                          </Typography>
                        ) : (
                          <List>
                            {messages.map((msg, idx) => (
                              <ListItem
                                key={msg.id || idx}
                                sx={{
                                  flexDirection: "column",
                                  alignItems:
                                    msg.direction === "outbound"
                                      ? "flex-end"
                                      : "flex-start",
                                  px: { xs: 1, sm: 2 },
                                  py: { xs: 0.5, sm: 1 },
                                }}
                              >
                                <Paper
                                  sx={{
                                    p: 1.5,
                                    maxWidth: {
                                      xs: "85%",
                                      sm: "75%",
                                      md: "70%",
                                    },
                                    bgcolor:
                                      msg.direction === "outbound"
                                        ? "primary.main"
                                        : "grey.100",
                                    color:
                                      msg.direction === "outbound"
                                        ? "white"
                                        : "text.primary",
                                  }}
                                >
                                  {msg.direction === "inbound" &&
                                    msg.authorName && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mb: 0.5 }}
                                      >
                                        {msg.authorName}
                                      </Typography>
                                    )}
                                  <Typography variant="body2">
                                    {msg.content}
                                  </Typography>
                                  {msg.attachments &&
                                    msg.attachments.length > 0 && (
                                      <Box
                                        sx={{
                                          mt: 1,
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1,
                                        }}
                                      >
                                        {msg.attachments.map(
                                          (
                                            attachment: MessageAttachment,
                                            attachmentIndex: number,
                                          ) => (
                                            <Box
                                              key={`${attachment.id || attachment.filename || attachmentIndex}`}
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              <AttachIcon fontSize="small" />
                                              <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                              >
                                                {attachment.filename ||
                                                  "Attachment"}
                                              </Typography>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() =>
                                                  handlePreviewAttachment(
                                                    attachment,
                                                    msg,
                                                  )
                                                }
                                              >
                                                View
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="text"
                                                onClick={() =>
                                                  handleDownloadAttachment(
                                                    attachment,
                                                    msg,
                                                  )
                                                }
                                              >
                                                Download
                                              </Button>
                                              {attachment.transcription && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  Transcription:{" "}
                                                  {attachment.transcription}
                                                </Typography>
                                              )}
                                            </Box>
                                          ),
                                        )}
                                      </Box>
                                    )}
                                </Paper>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {msg.timestamp &&
                                    formatDateTime(
                                      msg.timestamp,
                                      dateTimeFormat,
                                    )}
                                  {msg.direction === "outbound" &&
                                    msg.authorName &&
                                    ` • ${msg.authorName}`}
                                  {msg.direction === "outbound" &&
                                    msg.status &&
                                    ` • ${msg.status}`}
                                </Typography>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>

                      {/* Sticky composer at the bottom of the scroll container
                    on every viewport. iOS keyboard handling:
                    `position: sticky; bottom: 0` rides the visual viewport
                    on modern Safari/Chrome, so it sits right above the
                    on-screen keyboard without extra JS. Translucent fill
                    + backdrop blur keeps the conversation legible
                    underneath. The bleed (mx negative) makes the bar
                    span the full panel width even though the body
                    wrapper has padding. */}
                      <Box
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          zIndex: 3,
                          mx: { xs: -1.5, sm: -2 },
                          px: { xs: 1, sm: 1.5, md: 2 },
                          pt: { xs: 0.75, sm: 1 },
                          pb: {
                            xs: "max(env(safe-area-inset-bottom), 8px)",
                            sm: 1.25,
                          },
                          bgcolor: "rgba(255,255,255,0.92)",
                          backdropFilter: "saturate(180%) blur(10px)",
                          WebkitBackdropFilter: "saturate(180%) blur(10px)",
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        {
                          // Compact segmented mode toggle, shown on every
                          // viewport. Pills sit inline above the input so it's
                          // always obvious — at a glance — whether Send fires an
                          // SMS to the patient or saves a staff-only Internal
                          // Note (Krug's trunk test).
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              mb: 0.5,
                            }}
                          >
                            <ToggleButtonGroup
                              value={composeMode}
                              exclusive
                              size="small"
                              onChange={(_, v) => {
                                if (v) setComposeMode(v);
                              }}
                              aria-label="Compose as SMS or Internal Note"
                              sx={{
                                bgcolor: "grey.100",
                                borderRadius: 999,
                                p: 0.25,
                                "& .MuiToggleButton-root": {
                                  textTransform: "none",
                                  border: 0,
                                  px: 1.5,
                                  py: 0.25,
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  borderRadius: "999px !important",
                                  color: "text.secondary",
                                  "&.Mui-selected": {
                                    bgcolor: "background.paper",
                                    color: "primary.main",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                                  },
                                  '&.Mui-selected[value="note"]': {
                                    color: "warning.dark",
                                  },
                                },
                              }}
                            >
                              <ToggleButton
                                value="sms"
                                disabled={!selectedContact.phone}
                                aria-label="SMS mode"
                              >
                                <SmsIcon sx={{ mr: 0.5, fontSize: 14 }} />
                                SMS
                              </ToggleButton>
                              <ToggleButton
                                value="note"
                                aria-label="Internal note mode"
                              >
                                <NoteIcon sx={{ mr: 0.5, fontSize: 14 }} />
                                Note
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                        }
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.75,
                            alignItems: "flex-end",
                            flexWrap: { xs: "nowrap", sm: "nowrap" },
                          }}
                        >
                          {!isMobile && (
                            <Chip
                              icon={
                                isConnected ? (
                                  <WifiIcon fontSize="small" />
                                ) : (
                                  <WifiOffIcon fontSize="small" />
                                )
                              }
                              label={isConnected ? "Live" : "Offline"}
                              color={isConnected ? "success" : "default"}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <TextField
                            fullWidth
                            size="small"
                            multiline={isMobile}
                            minRows={isMobile ? 1 : undefined}
                            maxRows={isMobile ? 5 : undefined}
                            placeholder={
                              composeMode === "note"
                                ? "Add an internal note (not sent to patient)…"
                                : isMobile
                                  ? "Message patient"
                                  : "Type a message..."
                            }
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={(e) => {
                              // Enter sends only on desktop; mobile lets Enter add
                              // a newline so users can type multi-line texts the
                              // way they do in iMessage.
                              if (e.key !== "Enter" || e.shiftKey || isMobile)
                                return;
                              handleComposerSend();
                            }}
                            disabled={
                              composeMode === "sms" ? sendingMessage : savingNote
                            }
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              ...(isMobile
                                ? {
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 999,
                                      bgcolor: "background.paper",
                                      fontSize: "0.95rem",
                                      pl: 1.5,
                                      pr: 1,
                                      py: 0.25,
                                      "& fieldset": { borderColor: "divider" },
                                      "&:hover fieldset": {
                                        borderColor: "divider",
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor:
                                          composeMode === "note"
                                            ? "warning.main"
                                            : "primary.main",
                                        borderWidth: 1,
                                      },
                                    },
                                  }
                                : composeMode === "note"
                                  ? {
                                      // Desktop note-mode signifier: amber focus
                                      // ring so the active mode is unmistakable.
                                      "& .MuiOutlinedInput-root.Mui-focused fieldset":
                                        { borderColor: "warning.main" },
                                    }
                                  : {}),
                            }}
                          />
                          {isMobile ? (
                            (() => {
                              const busy =
                                composeMode === "sms"
                                  ? sendingMessage
                                  : savingNote;
                              const disabled =
                                busy ||
                                !newMessage.trim() ||
                                (composeMode === "sms" &&
                                  !selectedContact.phone);
                              return (
                                <IconButton
                                  onClick={handleComposerSend}
                                  disabled={disabled}
                                  aria-label={
                                    composeMode === "sms"
                                      ? "Send SMS"
                                      : "Save internal note"
                                  }
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    flexShrink: 0,
                                    bgcolor:
                                      composeMode === "sms"
                                        ? "primary.main"
                                        : "warning.main",
                                    color: "white",
                                    transition: "background-color 160ms ease",
                                    "&:hover": {
                                      bgcolor:
                                        composeMode === "sms"
                                          ? "primary.dark"
                                          : "warning.dark",
                                    },
                                    "&.Mui-disabled": {
                                      bgcolor: "action.disabledBackground",
                                      color: "action.disabled",
                                    },
                                  }}
                                >
                                  {busy ? (
                                    <CircularProgress
                                      size={16}
                                      sx={{ color: "white" }}
                                    />
                                  ) : (
                                    <SendIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                              );
                            })()
                          ) : (
                            <Button
                              variant="contained"
                              color={
                                composeMode === "note" ? "warning" : "primary"
                              }
                              onClick={handleComposerSend}
                              disabled={
                                (composeMode === "sms"
                                  ? sendingMessage
                                  : savingNote) ||
                                !newMessage.trim() ||
                                (composeMode === "sms" && !selectedContact.phone)
                              }
                              startIcon={
                                (
                                  composeMode === "sms"
                                    ? sendingMessage
                                    : savingNote
                                ) ? (
                                  <CircularProgress size={16} />
                                ) : composeMode === "note" ? (
                                  <NoteIcon />
                                ) : (
                                  <SendIcon />
                                )
                              }
                            >
                              {composeMode === "note" ? "Save Note" : "Send"}
                            </Button>
                          )}
                        </Box>
                        {!selectedContact.phone &&
                          composeMode === "sms" && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{
                                mt: 0.5,
                                display: "block",
                                textAlign: "center",
                              }}
                            >
                              No phone on file — switch to Note, or add a number
                              to send SMS.
                            </Typography>
                          )}
                      </Box>
                    </Box>
                  );
                })()}

              {/* Notes are merged into the Messages tab (composeMode toggle). */}

              {/* Billing Tab */}
              {activeTab === "billing" && (
                // Wrapped in a Paper card so it opens with the same framing +
                // buffer under the tab bar as the other tabs (Details'
                // Additional Info, Documents' summary panel, History) — the
                // bare outlined fields otherwise sat flush against the tabs.
                <Paper sx={{ p: { xs: 1.25, sm: 1.5 }, minWidth: 0 }}>
                  {/* Density pass: 2-up fields on every width (4 stacked
                      full-width rows read as a long form on a phone). */}
                  <Grid container spacing={1.25}>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Fee Structure</InputLabel>
                        <Select
                          value={billingForm.feeStructure}
                          label="Fee Structure"
                          onChange={(e) =>
                            setBillingForm((prev) => ({
                              ...prev,
                              feeStructure: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="">Not Set</MenuItem>
                          {FEE_STRUCTURE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Fee Amount"
                        type="number"
                        value={billingForm.feeAmount}
                        onChange={(e) =>
                          setBillingForm((prev) => ({
                            ...prev,
                            feeAmount: parseFloat(e.target.value) || 0,
                          }))
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Next Payment Due"
                        type="date"
                        value={
                          billingForm.nextPaymentDue
                            ? billingForm.nextPaymentDue.split("T")[0]
                            : ""
                        }
                        onChange={(e) =>
                          setBillingForm((prev) => ({
                            ...prev,
                            nextPaymentDue: e.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Payment Status</InputLabel>
                        <Select
                          value={billingForm.paymentStatus}
                          label="Payment Status"
                          onChange={(e) =>
                            setBillingForm((prev) => ({
                              ...prev,
                              paymentStatus: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="">Not Set</MenuItem>
                          {PAYMENT_STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {zapriteBillingDetails?.latestInvoice?.status && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          Zaprite status:{" "}
                          {zapriteBillingDetails.latestInvoice.status}
                          {zapriteBillingDetails.latestInvoice.number
                            ? ` • #${zapriteBillingDetails.latestInvoice.number}`
                            : ""}
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      {/* Save + quick actions in ONE wrapping row — the old
                          layout spent a heading + two dividers on them. */}
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveBilling}
                          disabled={savingBilling}
                          startIcon={
                            savingBilling ? (
                              <CircularProgress size={16} />
                            ) : (
                              <SaveIcon />
                            )
                          }
                        >
                          {savingBilling ? "Saving..." : "Save Billing Info"}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendIcon />}
                          onClick={openPaymentReminder}
                          disabled={!selectedContact.phone}
                        >
                          Send Payment Reminder
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MoneyIcon />}
                          onClick={openZapriteDialog}
                        >
                          Upload Invoice
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1.5 }} />

                  <Typography variant="subtitle2" gutterBottom>
                    Zaprite Billing Details
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {zapriteBillingLoading ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Syncing from Zaprite...
                        </Typography>
                      </Box>
                    ) : zapriteBillingError ? (
                      <Typography variant="caption" color="error">
                        {zapriteBillingError}
                      </Typography>
                    ) : zapriteBillingDetails?.billingDetails ? (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Email:{" "}
                          {zapriteBillingDetails.billingDetails.email || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Address:{" "}
                          {formatZapriteAddress(
                            zapriteBillingDetails.billingDetails.billingAddress,
                          ) || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last synced:{" "}
                          {zapriteBillingDetails?.lastSynced
                            ? formatDateTime(
                                zapriteBillingDetails.lastSynced,
                                dateTimeFormat,
                              )
                            : "—"}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Link a Zaprite ID to sync billing details.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              )}
            </Box>
          </Box>
        </Paper>
      ) : (
        !isMobile && (
          <Paper
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              Select a contact to view details
            </Typography>
          </Paper>
        )
      )}

      {/* Interactive Unarchive Dialog */}
      <Dialog
        open={unarchiveOpen}
        onClose={() => !unarchiveSubmitting && setUnarchiveOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unarchive contact</DialogTitle>
        <DialogContent dividers>
          {unarchiveLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                Checking Spruce for this contact…
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Restore{" "}
                <strong>{selectedContact?.fullName || "this contact"}</strong> to
                the active list. Choose how to reconcile with Spruce:
              </Typography>

              {unarchivePreview?.status === "linked" && (
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  Already linked to Spruce. It will be re-tagged{" "}
                  <strong>LevitanMD</strong> (ARCHIVED removed) and its latest
                  messages pulled.
                </Alert>
              )}
              {unarchivePreview?.status === "found" && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Found {unarchivePreview?.matches?.length} matching Spruce
                  contact
                  {unarchivePreview?.matches?.length > 1 ? "s" : ""} by
                  phone/email.
                </Alert>
              )}
              {unarchivePreview?.status === "not_found" && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  No matching Spruce contact found.
                </Alert>
              )}
              {unarchivePreview?.status === "no_identifier" && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  This contact has no phone or email to look up in Spruce.
                </Alert>
              )}
              {unarchivePreview?.status === "lookup_error" && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  Couldn't reach Spruce to check for a match.
                </Alert>
              )}

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={unarchiveAction}
                  onChange={(e) => setUnarchiveAction(e.target.value)}
                >
                  {unarchivePreview?.status === "linked" && (
                    <FormControlLabel
                      value="auto"
                      control={<Radio />}
                      label="Re-tag in Spruce and pull latest (recommended)"
                    />
                  )}
                  {unarchivePreview?.status === "found" &&
                    (unarchivePreview?.matches || []).map((m: any) => (
                      <FormControlLabel
                        key={m.id}
                        value={"link:" + m.id}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2">
                              Link to:{" "}
                              <strong>{m.displayName || "(unnamed)"}</strong>
                              {m.hasLevitanMd ? " (already LevitanMD)" : ""}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[m.phone, m.email].filter(Boolean).join(" · ")}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  {(unarchivePreview?.status === "not_found" ||
                    unarchivePreview?.status === "no_identifier" ||
                    unarchivePreview?.status === "found") && (
                    <FormControlLabel
                      value="create"
                      control={<Radio />}
                      label="Create a new LevitanMD contact in Spruce"
                    />
                  )}
                  <FormControlLabel
                    value="local_only"
                    control={<Radio />}
                    label="Just unarchive here (don't touch Spruce)"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnarchiveOpen(false)}
            disabled={unarchiveSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmUnarchive}
            disabled={unarchiveLoading || unarchiveSubmitting}
            startIcon={
              unarchiveSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <UnarchiveIcon />
              )
            }
          >
            {unarchiveSubmitting ? "Unarchiving…" : "Unarchive"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive/Unarchive Confirmation Dialog */}
      <Dialog
        open={archiveConfirmOpen}
        onClose={() => !archiving && setArchiveConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {selectedContact?.isArchived
            ? "Unarchive contact?"
            : "Archive contact?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {selectedContact?.isArchived
              ? `Restore ${selectedContact?.fullName || "this contact"} to the active list?`
              : `Move ${selectedContact?.fullName || "this contact"} to the archive? You can restore them later from the Archived filter.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setArchiveConfirmOpen(false)}
            disabled={archiving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={selectedContact?.isArchived ? "primary" : "warning"}
            onClick={performArchiveContact}
            disabled={archiving}
            startIcon={
              archiving ? (
                <CircularProgress size={16} color="inherit" />
              ) : selectedContact?.isArchived ? (
                <UnarchiveIcon />
              ) : (
                <ArchiveIcon />
              )
            }
          >
            {archiving
              ? selectedContact?.isArchived
                ? "Unarchiving…"
                : "Archiving…"
              : selectedContact?.isArchived
                ? "Unarchive"
                : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={closeContactModal}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {contactModalMode === "create" ? "Add Contact" : "Edit Contact"}
            <IconButton onClick={closeContactModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={editForm.firstName || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={editForm.lastName || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, lastName: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={editForm.phone || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editForm.email || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={
                  editForm.dateOfBirth
                    ? String(editForm.dateOfBirth).split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    dateOfBirth: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={editForm.gender || ""}
                  label="Gender"
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, gender: e.target.value }))
                  }
                >
                  <MenuItem value="">Not Set</MenuItem>
                  {GENDER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={editForm.addressLine1 || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    addressLine1: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={editForm.addressLine2 || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    addressLine2: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="City"
                value={editForm.city || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, city: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="State"
                value={editForm.state || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, state: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Zip Code"
                value={editForm.zipCode || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, zipCode: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeContactModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveContact}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving
              ? "Saving..."
              : contactModalMode === "create"
                ? "Create Contact"
                : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Reminder Modal */}
      <Dialog
        open={isBillingReminderOpen}
        onClose={() => setIsBillingReminderOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Send Payment Reminder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will send an SMS reminder to {selectedContact?.fullName} at{" "}
            {selectedContact?.phone}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={reminderMessage}
            onChange={(e) => setReminderMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBillingReminderOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendPaymentReminder}
            disabled={sendingReminder || !reminderMessage.trim()}
            startIcon={
              sendingReminder ? <CircularProgress size={16} /> : <SendIcon />
            }
          >
            {sendingReminder ? "Sending..." : "Send via Spruce"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zaprite Billing Modal */}
      <Dialog
        open={isZapriteDialogOpen}
        onClose={handleCloseZapriteDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Zaprite Billing</DialogTitle>
        <DialogContent>
          <Tabs
            value={zapriteTab}
            onChange={(e, value) => setZapriteTab(value)}
            sx={{ mb: 2 }}
          >
            <Tab label="Import from Zaprite" value="import" />
            <Tab label="Upload PDF" value="upload" />
          </Tabs>

          {zapriteTab === "import" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {zapriteLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : zapriteLink?.external_customer_id ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Linked Zaprite Customer ID:
                  </Typography>
                  {!isZapriteEditing ? (
                    <>
                      <Typography variant="subtitle2">
                        {zapriteLink.external_customer_id}
                      </Typography>
                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={startZapriteEdit}
                        >
                          Change Zaprite ID
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <TextField
                        label="Zaprite Customer ID"
                        value={zapriteCustomerId}
                        onChange={(e) => setZapriteCustomerId(e.target.value)}
                        fullWidth
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="contained"
                          onClick={handleZapriteLink}
                          disabled={zapriteLinking || !zapriteCustomerId.trim()}
                        >
                          {zapriteLinking ? "Saving..." : "Save Zaprite ID"}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={cancelZapriteEdit}
                          disabled={zapriteLinking}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Link this contact to a Zaprite customer before importing
                    invoices.
                  </Typography>
                  <TextField
                    label="Zaprite Customer ID"
                    value={zapriteCustomerId}
                    onChange={(e) => setZapriteCustomerId(e.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    onClick={handleZapriteLink}
                    disabled={zapriteLinking || !zapriteCustomerId.trim()}
                  >
                    {zapriteLinking ? "Linking..." : "Link Zaprite Customer"}
                  </Button>
                </Box>
              )}

              {zapriteError && <Alert severity="error">{zapriteError}</Alert>}

              {zapriteLink?.external_customer_id && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={loadZapriteInvoices}
                    disabled={zapriteInvoicesLoading}
                  >
                    {zapriteInvoicesLoading ? "Loading..." : "Refresh Invoices"}
                  </Button>
                  {zapriteInvoicesMessage && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {zapriteInvoicesMessage}
                    </Typography>
                  )}
                  {!zapriteInvoicesLoading &&
                    zapriteInvoices.length === 0 &&
                    !zapriteInvoicesMessage && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        No Zaprite invoices found.
                      </Typography>
                    )}
                  {!zapriteInvoicesLoading && zapriteInvoices.length > 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {zapriteInvoices.map((order) => {
                        const invoice = order?.invoice || {};
                        const title = invoice?.number
                          ? `Invoice #${invoice.number}`
                          : order?.label || order?.id;
                        const status =
                          invoice?.status || order?.status || "Unknown";
                        const amount = formatZapriteAmount(
                          order?.totalAmount,
                          order?.currency,
                        );
                        const dateLabel =
                          invoice?.date ||
                          order?.paidAt ||
                          order?.expiresAt ||
                          "";
                        const viewUrl =
                          invoice?.publicUrl ||
                          order?.checkoutUrl ||
                          order?.redirectUrl ||
                          "";
                        const receiptUrl = order?.receiptPdfUrl || "";
                        return (
                          <Box
                            key={order.id}
                            sx={{
                              p: 1,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Typography variant="subtitle2">
                                {title}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {status}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {amount}
                              {dateLabel ? ` • ${dateLabel}` : ""}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                mt: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {viewUrl && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    window.open(viewUrl, "_blank", "noopener")
                                  }
                                >
                                  View Invoice
                                </Button>
                              )}
                              {receiptUrl && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    window.open(
                                      receiptUrl,
                                      "_blank",
                                      "noopener",
                                    )
                                  }
                                >
                                  Receipt PDF
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => openZapriteSendDialog(order)}
                                disabled={!selectedContact?.phone}
                              >
                                Send via SMS
                              </Button>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {zapriteTab === "upload" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Upload PDF invoices (manual entry). Zaprite import remains
                separate.
              </Typography>
              <Button variant="outlined" disabled>
                Upload PDF (coming soon)
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseZapriteDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Zaprite Send Invoice SMS Modal */}
      <Dialog
        open={zapriteSendDialogOpen}
        onClose={closeZapriteSendDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Send Invoice via SMS</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Send to {selectedContact?.fullName || "patient"}{" "}
              {selectedContact?.phone ? `at ${selectedContact.phone}` : ""}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {zapriteSendOrder?.invoice?.number
                ? `Invoice #${zapriteSendOrder.invoice.number}`
                : zapriteSendOrder?.label || "Invoice"}
            </Typography>
            {zapriteSendOrder?.invoice?.publicUrl && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ wordBreak: "break-word" }}
              >
                Link: {zapriteSendOrder.invoice.publicUrl}
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Message"
              value={zapriteSendMessage}
              onChange={(e) => setZapriteSendMessage(e.target.value)}
              placeholder="Type the message to send"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeZapriteSendDialog}
            disabled={zapriteSendLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleZapriteSendSms}
            disabled={zapriteSendLoading || !zapriteSendMessage.trim()}
          >
            {zapriteSendLoading ? "Sending..." : "Send via SMS"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(attachmentPreview)}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{attachmentPreview?.filename || "Attachment"}</DialogTitle>
        <DialogContent sx={{ minHeight: 200 }}>
          {isPreviewLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!isPreviewLoading && attachmentPreview && (
            <>
              {attachmentPreviewText ? (
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: "0.9rem",
                    bgcolor: "grey.50",
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  {attachmentPreviewText}
                </Box>
              ) : attachmentPreview.contentType?.startsWith("image/") ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Box
                    component="img"
                    src={attachmentPreview.url}
                    alt={attachmentPreview.filename}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      borderRadius: 1,
                    }}
                  />
                </Box>
              ) : attachmentPreview.contentType === "application/pdf" ? (
                <Box
                  component="iframe"
                  src={attachmentPreview.url}
                  sx={{ width: "100%", height: "70vh", border: "none" }}
                />
              ) : attachmentPreview.contentType?.startsWith("audio/") ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <audio
                    controls
                    src={attachmentPreview.url}
                    style={{ width: "100%" }}
                  />
                  {attachmentPreview.transcription && (
                    <Typography variant="body2" color="text.secondary">
                      Transcription: {attachmentPreview.transcription}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Preview unavailable. Click to view/download.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {attachmentPreview?.url && (
            <Button
              onClick={() =>
                window.open(attachmentPreview.url, "_blank", "noopener")
              }
            >
              Open in New Tab
            </Button>
          )}
          {attachmentPreviewSource && (
            <Button
              onClick={() =>
                handleDownloadAttachment(
                  attachmentPreviewSource.attachment,
                  attachmentPreviewSource.message,
                )
              }
            >
              Download
            </Button>
          )}
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Appointment scheduler — opened from an AI scheduling suggestion,
          pre-filled for the current patient. */}
      <AppointmentModal
        open={appointmentModalOpen}
        appointment={null}
        initialPatient={appointmentInitialPatient}
        initialDraft={appointmentDraft}
        onClose={() => setAppointmentModalOpen(false)}
        onSave={(appt) => {
          setAppointmentModalOpen(false);
          showSnackbar(
            `Appointment created for ${appt.patientName || "patient"}`,
            "success",
          );
        }}
      />
    </Box>
  );
};

export default Contacts;
